// src/pages/TradingDashboard.js
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import {
  LogOut,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowDownToLine,
  Menu,
  MessageCircle,
  UserRound,
  BellRing,
  CheckCircle2,
  LockKeyhole,
  ShieldCheck,
  Clock3,
  RefreshCw,
  X,
} from "lucide-react";

import DepositModal from "../components/DepositModal";
import WithdrawalModal from "../components/WithdrawalModal";
import OrdersTable from "../components/OrdersTable";
import CofyLockPanel from "../components/CofyLockPanel";

const DEFAULT_KC_SYMBOL = "KCK6";
const LBS_PER_60KG_SACK = 132.2772;
// carimbo pra conferir se esta versão está na tela
const BUILD_TAG = "TD_LOCK_LIVE_V21_2026-10-06";

// ===== helpers =====
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0,00";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatBrl(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatQuote(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function moneyMaskFromDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return { display: "", numeric: "" };
  const cents = Number(digits);
  const amount = cents / 100;
  return { display: formatBrl(amount), numeric: String(amount) };
}
function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}
function readTokenFromStorage() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}
function normalizeBearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return t.toLowerCase().startsWith("bearer ") ? t : `Bearer ${t}`;
}
function applyAxiosAuthHeader(tokenLike) {
  const token = String(tokenLike || "").trim();
  if (token) {
    axios.defaults.headers.common.Authorization = normalizeBearer(token);
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}
async function safeGet(url, options = {}) {
  const res = await axios.get(url, {
    timeout: 2500,
    validateStatus: () => true,
    ...options,
  });
  return res;
}
async function probeApiBase(baseUrl) {
  const endpoints = ["/ping", "/health", "/status"];
  for (const ep of endpoints) {
    try {
      const r = await safeGet(`${baseUrl}${ep}`);
      if (r?.status === 200) return true;
    } catch (_) {}
  }
  return false;
}
function buildOriginCandidates() {
  const origin = window.location.origin;
  const u = new URL(origin);
  const host = u.hostname;

  const candidates = new Set([origin]);

  if (host.startsWith("www.")) {
    candidates.add(`${u.protocol}//${host.replace(/^www\./, "")}`);
  } else {
    candidates.add(`${u.protocol}//www.${host}`);
  }

  // reforço
  candidates.add(`${u.protocol}//cofy.app`);
  candidates.add(`${u.protocol}//www.cofy.app`);

  return Array.from(candidates);
}
function join(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  return `${b}${path}`;
}

function getLockApiBase() {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    if (host === "cofy.app" || host === "www.cofy.app") {
      return `${window.location.origin}/api/lock`;
    }
  } catch (_) {}

  return "https://cofy.app/api/lock";
}


// ✅ axios SEM Authorization para cotações (evita CORS/preflight quando muda www <-> sem www)
const quotesAxios = axios.create({
  timeout: 5000,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

// tenta achar uma base que funcione pros DOIS arquivos (kc e dol)
async function resolveQuotesBase() {
  const origin = window.location.origin;

  // ordem: primeiro same-origin (local/prod), depois fallbacks absolutos
  const bases = [
    `${origin}/data`,
    `${origin}/api/data`,
    "https://www.cofy.app/api/data",
    "https://cofy.app/api/data",
  ];

  for (const base of bases) {
    try {
      const [kc, dol] = await Promise.all([
        quotesAxios.get(join(base, "/oncafe_kc.json"), { validateStatus: () => true }),
        quotesAxios.get(join(base, "/oncafe_dol.json"), { validateStatus: () => true }),
      ]);

      if (kc?.status !== 200 || dol?.status !== 200) continue;

      const kcPrice = Number(kc?.data?.price);
      const dolPrice = Number(dol?.data?.price);

      if (Number.isFinite(kcPrice) && kcPrice > 0 && Number.isFinite(dolPrice) && dolPrice > 0) {
        return base; // ✅ achou
      }
    } catch (_) {
      // continua tentando
    }
  }

  return ""; // não achou
}


function ProfessionalCoffeeChart({ candles = [], symbol = "", error = "" }) {
  const hostRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef({});
  const [tf, setTf] = useState(1);
  const [chartType, setChartType] = useState(() => {
    try {
      const saved = localStorage.getItem("cofyTraderChartType");
      return ["candles", "line", "area"].includes(saved) ? saved : "candles";
    } catch (_) {
      return "candles";
    }
  });
  const [libReady, setLibReady] = useState(() => Boolean(window?.LightweightCharts));
  const [chartReady, setChartReady] = useState(false);
  const [renderError, setRenderError] = useState("");

  const normalize = useCallback((rows) => {
    const sorted = (Array.isArray(rows) ? rows : [])
      .map((c) => {
        // API COFY envia `t` em milissegundos e `time` como ISO.
        // Prioriza `t`; se não existir, converte o ISO corretamente.
        let time = Number(c?.t ?? 0);
        if (!Number.isFinite(time) || time <= 0) {
          const parsed = Date.parse(c?.time || "");
          time = Number.isFinite(parsed) ? parsed : 0;
        }
        if (time > 20000000000) time = Math.floor(time / 1000);
        return {
          time: Math.floor(time),
          open: toNumber(c?.open ?? c?.o, 0),
          high: toNumber(c?.high ?? c?.h, 0),
          low: toNumber(c?.low ?? c?.l, 0),
          close: toNumber(c?.close ?? c?.c, 0),
          volume: toNumber(c?.volume, 0),
        };
      })
      .filter((c) =>
        Number.isFinite(c.time) &&
        c.time > 0 &&
        c.open > 0 &&
        c.high > 0 &&
        c.low > 0 &&
        c.close > 0
      )
      .sort((a, b) => a.time - b.time);

    const byTime = new Map();
    sorted.forEach((c) => byTime.set(c.time, c));
    return Array.from(byTime.values()).sort((a, b) => a.time - b.time);
  }, []);

  const aggregate = useCallback((rows, minutes) => {
    const data = normalize(rows);
    if (minutes === 1) return data;
    const sec = minutes * 60;
    const map = new Map();
    data.forEach((c) => {
      const t = Math.floor(c.time / sec) * sec;
      if (!map.has(t)) {
        map.set(t, { ...c, time: t });
      } else {
        const b = map.get(t);
        b.high = Math.max(b.high, c.high);
        b.low = Math.min(b.low, c.low);
        b.close = c.close;
        b.volume = Math.max(b.volume || 0, c.volume || 0);
      }
    });
    return [...map.values()].sort((a, b) => a.time - b.time);
  }, [normalize]);

  const ema = useCallback((data, period) => {
    if (!data.length) return [];
    const k = 2 / (period + 1);
    let value = data[0].close;
    return data.map((c, i) => {
      value = i === 0 ? c.close : c.close * k + value * (1 - k);
      return { time: c.time, value };
    });
  }, []);

  // Carrega a biblioteca uma única vez e registra falha real no console/tela.
  useEffect(() => {
    if (window?.LightweightCharts) {
      setLibReady(true);
      return;
    }

    let disposed = false;
    let script = document.querySelector('script[data-cofy-lightweight="1"]');

    const loaded = () => {
      if (!disposed && window?.LightweightCharts) {
        setRenderError("");
        setLibReady(true);
      }
    };
    const failed = () => {
      if (!disposed) {
        console.error("[COFY CHART] biblioteca Lightweight Charts não carregou.");
        setRenderError("Biblioteca do gráfico não carregou.");
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.src = "https://unpkg.com/lightweight-charts@5.2.0/dist/lightweight-charts.standalone.production.js";
      script.async = true;
      script.dataset.cofyLightweight = "1";
      document.head.appendChild(script);
    }

    script.addEventListener("load", loaded);
    script.addEventListener("error", failed);
    if (window?.LightweightCharts) loaded();

    return () => {
      disposed = true;
      script?.removeEventListener("load", loaded);
      script?.removeEventListener("error", failed);
    };
  }, []);

  // Inicializa o gráfico apenas quando o host desktop possui dimensões reais.
  useEffect(() => {
    if (!libReady || !window?.LightweightCharts) return;

    let disposed = false;
    let ro = null;
    let timer = null;
    let localChart = null;

    const init = () => {
      if (disposed || chartRef.current) return;

      const host = hostRef.current;
      if (!host) {
        timer = window.setTimeout(init, 100);
        return;
      }

      const rect = host.getBoundingClientRect();
      const width = Math.max(0, Math.floor(rect.width || host.clientWidth || 0));
      const height = Math.max(0, Math.floor(rect.height || host.clientHeight || 0));

      if (width < 80 || height < 120) {
        timer = window.setTimeout(init, 120);
        return;
      }

      try {
        const LC = window.LightweightCharts;
        localChart = LC.createChart(host, {
          width,
          height,
          layout: {
            background: { type: LC.ColorType?.Solid ?? "solid", color: "#020617" },
            textColor: "#94a3b8",
            attributionLogo: false,
          },
          grid: {
            vertLines: { color: "rgba(148,163,184,.07)" },
            horzLines: { color: "rgba(148,163,184,.07)" },
          },
          rightPriceScale: {
            borderColor: "#1e293b",
            scaleMargins: { top: 0.08, bottom: 0.25 },
          },
          timeScale: {
            borderColor: "#1e293b",
            timeVisible: true,
            secondsVisible: false,
            rightOffset: 5,
            barSpacing: 8,
          },
          crosshair: { mode: LC.CrosshairMode?.Normal ?? 0 },
          handleScroll: true,
          handleScale: true,
        });

        // Compatibilidade explícita com Lightweight Charts v5 e fallback v4.
        const addSeriesCompat = (seriesType, oldMethod, options) => {
          if (typeof localChart.addSeries === "function" && seriesType) {
            return localChart.addSeries(seriesType, options);
          }
          if (typeof localChart[oldMethod] === "function") {
            return localChart[oldMethod](options);
          }
          throw new Error(`Série não suportada: ${oldMethod}`);
        };

        const candle = addSeriesCompat(LC.CandlestickSeries, "addCandlestickSeries", {
          upColor: "#20e39d",
          downColor: "#ff6178",
          borderUpColor: "#20e39d",
          borderDownColor: "#ff6178",
          wickUpColor: "#20e39d",
          wickDownColor: "#ff6178",
          priceFormat: { type: "price", precision: 3, minMove: 0.025 },
        });

        const line = addSeriesCompat(LC.LineSeries, "addLineSeries", {
          lineWidth: 2,
          visible: false,
          priceFormat: { type: "price", precision: 3, minMove: 0.025 },
        });

        const area = addSeriesCompat(LC.AreaSeries, "addAreaSeries", {
          lineWidth: 2,
          visible: false,
          priceFormat: { type: "price", precision: 3, minMove: 0.025 },
        });

        const ema9 = addSeriesCompat(LC.LineSeries, "addLineSeries", {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        const ema21 = addSeriesCompat(LC.LineSeries, "addLineSeries", {
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });

        const volume = addSeriesCompat(LC.HistogramSeries, "addHistogramSeries", {
          priceFormat: { type: "volume" },
          priceScaleId: "",
          lastValueVisible: false,
          priceLineVisible: false,
        });

        volume.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

        chartRef.current = localChart;
        seriesRef.current = { candle, line, area, ema9, ema21, volume };
        setRenderError("");
        setChartReady(true);

        const resize = () => {
          if (!hostRef.current || !chartRef.current) return;
          const r = hostRef.current.getBoundingClientRect();
          const w = Math.floor(r.width || hostRef.current.clientWidth || 0);
          const h = Math.floor(r.height || hostRef.current.clientHeight || 0);
          if (w >= 80 && h >= 120) {
            chartRef.current.applyOptions({ width: w, height: h });
          }
        };

        ro = new ResizeObserver(resize);
        ro.observe(host);
        resize();
      } catch (e) {
        console.error("[COFY CHART] falha ao inicializar:", e);
        setRenderError(e?.message || "Falha ao inicializar gráfico.");
        setChartReady(false);
        try { localChart?.remove(); } catch (_) {}
        localChart = null;
        chartRef.current = null;
        seriesRef.current = {};
      }
    };

    init();

    return () => {
      disposed = true;
      if (timer) window.clearTimeout(timer);
      if (ro) ro.disconnect();
      try { localChart?.remove(); } catch (_) {}
      if (chartRef.current === localChart) chartRef.current = null;
      seriesRef.current = {};
      setChartReady(false);
    };
  }, [libReady]);

  // Alimenta as séries somente depois que elas realmente existem.
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;
    const s = seriesRef.current;
    if (!s?.candle || !s?.line || !s?.area || !s?.ema9 || !s?.ema21 || !s?.volume) return;

    const data = aggregate(candles, tf);
    if (!data.length) return;

    try {
      const closeData = data.map((x) => ({ time: x.time, value: x.close }));

      s.candle.setData(data.map(({ time, open, high, low, close }) => ({
        time, open, high, low, close
      })));
      s.line.setData(closeData);
      s.area.setData(closeData);
      s.ema9.setData(ema(data, 9));
      s.ema21.setData(ema(data, 21));

      let prev = null;
      const volumeData = data.map((x) => {
        const delta = prev !== null && x.volume >= prev ? x.volume - prev : 0;
        prev = x.volume;
        return { time: x.time, value: Math.max(0, delta) };
      });
      s.volume.setData(volumeData);

      s.candle.applyOptions({ visible: chartType === "candles" });
      s.line.applyOptions({ visible: chartType === "line" });
      s.area.applyOptions({ visible: chartType === "area" });

      chartRef.current.timeScale().fitContent();
      setRenderError("");
    } catch (e) {
      console.error("[COFY CHART] falha ao aplicar candles:", e);
      setRenderError(e?.message || "Falha ao desenhar candles.");
    }
  }, [candles, tf, chartType, aggregate, ema, chartReady]);

  const data = aggregate(candles, tf);
  const last = data[data.length - 1];
  const first = data[0];
  const delta = last && first ? last.close - first.open : 0;

  const chooseType = (type) => {
    setChartType(type);
    try { localStorage.setItem("cofyTraderChartType", type); } catch (_) {}
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/60">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-3 py-2.5">
        <div>
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-500">Gráfico profissional • COFY</p>
          <p className="mt-0.5 text-xs font-bold text-slate-200">{symbol || "Café NY"} • {tf} min</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {[1, 5, 15].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setTf(m)}
              className={`rounded-lg border px-2.5 py-1.5 text-[10px] font-black ${tf === m ? "border-emerald-400 bg-emerald-400 text-slate-950" : "border-slate-700 bg-slate-900 text-slate-400"}`}
            >
              {m}m
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-800" />
          {[
            ["candles", "VELAS"],
            ["line", "LINHA"],
            ["area", "ÁREA"],
          ].map(([type, label]) => (
            <button
              key={type}
              type="button"
              onClick={() => chooseType(type)}
              className={`rounded-lg border px-2 py-1.5 text-[9px] font-black ${chartType === type ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-slate-700 bg-slate-900 text-slate-500"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && data.length < 2 ? (
        <div className="flex h-[300px] items-center justify-center text-center">
          <div>
            <p className="text-xs font-bold text-slate-300">Gráfico indisponível</p>
            <p className="mt-1 text-[10px] text-slate-600">{error}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="relative">
            <div
              ref={hostRef}
              className="h-[330px] w-full sm:h-[390px] lg:h-[455px]"
              style={{ minHeight: 330 }}
            />
            {renderError ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/80 px-4 text-center">
                <div>
                  <p className="text-xs font-bold text-red-300">Falha ao desenhar o gráfico</p>
                  <p className="mt-1 text-[10px] text-slate-500">{renderError}</p>
                </div>
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-3 border-t border-slate-800 bg-slate-950/70 text-center">
            <div className="px-2 py-2">
              <span className="block text-[8px] uppercase tracking-wider text-slate-600">EMA 9</span>
              <strong className="text-[10px] text-cyan-200">ATIVA</strong>
            </div>
            <div className="border-x border-slate-800 px-2 py-2">
              <span className="block text-[8px] uppercase tracking-wider text-slate-600">EMA 21</span>
              <strong className="text-[10px] text-violet-200">ATIVA</strong>
            </div>
            <div className="px-2 py-2">
              <span className="block text-[8px] uppercase tracking-wider text-slate-600">Movimento</span>
              <strong className={`text-[10px] ${delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {delta >= 0 ? "+" : ""}{delta.toFixed(2)}
              </strong>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


function MobileCoffeeSparkline({ candles = [] }) {
  const points = (Array.isArray(candles) ? candles : [])
    .slice(-70)
    .map((c) => toNumber(c?.close ?? c?.c, 0))
    .filter((v) => v > 0);

  if (points.length < 2) {
    return (
      <div className="flex h-[76px] items-center justify-center rounded-xl border border-slate-800 bg-slate-950/50 text-[10px] text-slate-600">
        Carregando movimento do café...
      </div>
    );
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(max - min, 0.0001);
  const w = 320;
  const h = 76;
  const pad = 4;
  const coords = points.map((v, i) => {
    const x = pad + (i / Math.max(points.length - 1, 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const line = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;
  const rising = points[points.length - 1] >= points[0];

  return (
    <div className="relative h-[76px] overflow-hidden rounded-xl border border-slate-800 bg-slate-950/55">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id="cofySparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={rising ? "#20e39d" : "#ff6178"} stopOpacity="0.20" />
            <stop offset="100%" stopColor={rising ? "#20e39d" : "#ff6178"} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#cofySparkFill)" />
        <polyline
          points={line}
          fill="none"
          stroke={rising ? "#20e39d" : "#ff6178"}
          strokeWidth="2.4"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="absolute left-2 top-1.5 text-[8px] font-bold uppercase tracking-[0.16em] text-slate-600">
        Movimento recente
      </div>
    </div>
  );
}

function PricePercentSlider({
  label,
  hint = "",
  value,
  onChange,
  referencePrice,
  disabled = false,
  accent = "cyan",
  minPct = -10,
  maxPct = 10,
  step = 0.1,
}) {
  const ref = toNumber(referencePrice, 0);
  const price = Number(String(value || "").replace(",", "."));
  const hasPrice = Number.isFinite(price) && price > 0 && ref > 0;
  const rawPct = hasPrice ? ((price / ref) - 1) * 100 : 0;
  const sliderPct = Math.max(minPct, Math.min(maxPct, rawPct));

  const accentClasses = accent === "red"
    ? "text-red-300 border-red-500/25 bg-red-500/10"
    : accent === "emerald"
    ? "text-emerald-300 border-emerald-500/25 bg-emerald-500/10"
    : "text-cyan-300 border-cyan-500/25 bg-cyan-500/10";

  const updateFromPercent = (pct) => {
    if (disabled || ref <= 0) return;
    const nextPct = Number(pct);
    const nextPrice = ref * (1 + nextPct / 100);
    if (Number.isFinite(nextPrice) && nextPrice > 0) {
      onChange(nextPrice.toFixed(2));
    }
  };

  const handleTextChange = (raw) => {
    onChange(String(raw || "").replace(/[^0-9.,]/g, ""));
  };

  const pctLabel = hasPrice
    ? `${rawPct >= 0 ? "+" : ""}${rawPct.toFixed(2)}%`
    : "0,00%";

  return (
    <div className={`rounded-xl border border-slate-800 bg-slate-950/50 p-3 ${disabled ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold text-slate-200">{label}</p>
          {hint ? <p className="mt-0.5 text-[8px] text-slate-600">{hint}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded-lg border px-2 py-1 text-[10px] font-black ${accentClasses}`}>
            {pctLabel}
          </span>
          <input
            disabled={disabled}
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Preço"
            className="w-24 rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-right text-[11px] font-bold text-white outline-none focus:border-cyan-400/60 disabled:opacity-30"
          />
        </div>
      </div>

      <div className="mt-3">
        <input
          disabled={disabled || ref <= 0}
          type="range"
          min={minPct}
          max={maxPct}
          step={step}
          value={sliderPct}
          onChange={(e) => updateFromPercent(e.target.value)}
          className="h-2 w-full cursor-pointer accent-cyan-400 disabled:cursor-not-allowed"
          aria-label={`${label} em porcentagem`}
        />
        <div className="mt-1 flex items-center justify-between text-[8px] font-semibold text-slate-600">
          <span>{minPct}%</span>
          <button
            type="button"
            disabled={disabled || ref <= 0}
            onClick={() => updateFromPercent(0)}
            className="rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-800 hover:text-white disabled:opacity-30"
          >
            0% • {ref > 0 ? formatBrl(ref) : "--"}
          </button>
          <span>+{maxPct}%</span>
        </div>
      </div>
    </div>
  );
}

export default function TradingDashboard({ user, onLogout }) {
  // ===== estado =====
  const [balance, setBalance] = useState(0);
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [totalCofyQty, setTotalCofyQty] = useState(0);

  // ===== Conta REAL / DEMO =====
  const [accountMode, setAccountMode] = useState(() => {
    try {
      return localStorage.getItem("cofy_trader_account_mode") === "demo" ? "demo" : "real";
    } catch (_) {
      return "real";
    }
  });
  const [demoBalance, setDemoBalance] = useState(10000);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState("");
  const [demoOrders, setDemoOrders] = useState([]);
  const [demoClosingId, setDemoClosingId] = useState(null);

  const [marketData, setMarketData] = useState(null);
  const [orders, setOrders] = useState([]);

  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);

  // ===== Cadastro para saque =====
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [withdrawProfileCompleted, setWithdrawProfileCompleted] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    cpf: "",
    phone: "",
    birth_date: "",
    city: "",
    state: "",
    pix_key_type: "cpf",
    pix_key: "",
  });

  const [userSettings, setUserSettings] = useState({
    min_close_time_minutes: 1440,
    spread_percentage: 0.2,
  });

  const [tradeSide, setTradeSide] = useState("buy");
  const [tradeAmount, setTradeAmount] = useState("");
  const [tradeError, setTradeError] = useState("");
  const [buyLoading, setBuyLoading] = useState(false);
  const [sellLoading, setSellLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showCofyLock, setShowCofyLock] = useState(false);

  // ===== Resumo COFY LOCK na tela principal =====
  const [lockDashboardContracts, setLockDashboardContracts] = useState([]);
  const [lockDashboardLoading, setLockDashboardLoading] = useState(false);
  const [lockDashboardUpdatedAt, setLockDashboardUpdatedAt] = useState(null);

  const [pendingTradeConfirm, setPendingTradeConfirm] = useState(null);
  const [showTradePanel, setShowTradePanel] = useState(false);
  // Ordens avançadas habilitadas para conta REAL e DEMO.
  const [orderType, setOrderType] = useState("market");
  const [triggerPrice, setTriggerPrice] = useState("");
  const [stopLossEnabled, setStopLossEnabled] = useState(false);
  const [stopLossPrice, setStopLossPrice] = useState("");
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
  const [takeProfitPrice, setTakeProfitPrice] = useState("");
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [operationsTab, setOperationsTab] = useState("open");
  const [cancelingPendingId, setCancelingPendingId] = useState(null);

  // ===== Gerenciamento de ordens pendentes LIMIT / STOP =====
  const [pendingEditOrder, setPendingEditOrder] = useState(null);
  const [pendingEditType, setPendingEditType] = useState("limit");
  const [pendingEditTrigger, setPendingEditTrigger] = useState("");
  const [pendingEditStopLoss, setPendingEditStopLoss] = useState("");
  const [pendingEditTakeProfit, setPendingEditTakeProfit] = useState("");
  const [pendingEditSaving, setPendingEditSaving] = useState(false);
  const [pendingEditError, setPendingEditError] = useState("");

  // ===== Gerenciamento explícito de posição ABERTA =====
  const [openManageOrder, setOpenManageOrder] = useState(null);
  const [openManageStopLoss, setOpenManageStopLoss] = useState("");
  const [openManageTakeProfit, setOpenManageTakeProfit] = useState("");
  const [openManageSaving, setOpenManageSaving] = useState(false);
  const [openManageError, setOpenManageError] = useState("");

  const [lastQuoteUpdateAt, setLastQuoteUpdateAt] = useState(0);

  // ===== Avisos enviados pelo Admin =====
  const [userNotices, setUserNotices] = useState([]);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeConfirming, setNoticeConfirming] = useState(false);

  // ===== Conta Parceira =====
  const [partnerData, setPartnerData] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const [newPartnerCommissions, setNewPartnerCommissions] = useState(0);

  // ===== Atendimento COFY via WhatsApp =====
  const openCofyWhatsApp = useCallback(() => {
    const phone = "5511912017765";
    const message = "Olá COFY! Estou no Trader e gostaria de atendimento.";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // ✅ cotações do TRADER (RAW)
  const [kcData, setKcData] = useState(null); // Arábica KC
  const [kcDisplayCents, setKcDisplayCents] = useState(0);
  const [rcData, setRcData] = useState(null); // Conilon RC, US$/t
  const [selectedInstrument, setSelectedInstrument] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const queryInstrument = String(params.get("instrument") || "").toUpperCase();
    const querySymbol = String(params.get("symbol") || "").toUpperCase();

    if (
      queryInstrument === "CAFE_CONILON" ||
      queryInstrument === "CONILON" ||
      querySymbol.startsWith("RC")
    ) {
      try { localStorage.setItem("cofy_trader_instrument", "CAFE_CONILON"); } catch (_) {}
      return "CAFE_CONILON";
    }

    if (
      queryInstrument === "CAFE_ARABICA" ||
      queryInstrument === "ARABICA" ||
      querySymbol.startsWith("KC")
    ) {
      try { localStorage.setItem("cofy_trader_instrument", "CAFE_ARABICA"); } catch (_) {}
      return "CAFE_ARABICA";
    }

    return localStorage.getItem("cofy_trader_instrument") === "CAFE_CONILON"
      ? "CAFE_CONILON"
      : "CAFE_ARABICA";
  });
  const [usdBrl, setUsdBrl] = useState(0);
  const [selectedKcSymbol, setSelectedKcSymbol] = useState(DEFAULT_KC_SYMBOL);
  const [chartCandles, setChartCandles] = useState([]);
  const [chartError, setChartError] = useState("");

  const [apiBase, setApiBase] = useState(localStorage.getItem("api_base_used") || "");
  const [quotesBase, setQuotesBase] = useState(localStorage.getItem("quotes_base_used") || "");

  const lastGoodUsdRef = useRef(0);
  const lastGoodKcRef = useRef(null);
  const lastGoodKcDisplayRef = useRef(0);
  const lastGoodRcRef = useRef(null);

  const lastQuotesBaseRef = useRef("");
  const lastWarnAtRef = useRef(0);

  // ===== animações leves (flash) =====
  const prevKcRef = useRef(null);
  const prevUsdRef = useRef(null);
  const [kcFlash, setKcFlash] = useState(null); // "up" | "down" | null
  const [usdFlash, setUsdFlash] = useState(null); // "up" | "down" | null

  useEffect(() => {
    if (!(kcDisplayCents > 0)) return;

    const curr = Number(kcDisplayCents);
    const prev = prevKcRef.current;

    if (Number.isFinite(prev) && prev > 0 && curr !== prev) {
      setKcFlash(curr > prev ? "up" : "down");
      const t = setTimeout(() => setKcFlash(null), 700);
      prevKcRef.current = curr;
      return () => clearTimeout(t);
    }

    prevKcRef.current = curr;
  }, [kcDisplayCents]);

  useEffect(() => {
    if (!(usdBrl > 0)) return;

    const curr = Number(usdBrl);
    const prev = prevUsdRef.current;

    if (Number.isFinite(prev) && prev > 0 && curr !== prev) {
      setUsdFlash(curr > prev ? "up" : "down");
      const t = setTimeout(() => setUsdFlash(null), 700);
      prevUsdRef.current = curr;
      return () => clearTimeout(t);
    }

    prevUsdRef.current = curr;
  }, [usdBrl]);

  // ===== AUTH =====
  useEffect(() => {
    const tokenFromUser = user?.access_token || user?.token || user?.jwt || "";
    const token = tokenFromUser || readTokenFromStorage();
    applyAxiosAuthHeader(token);
  }, [user]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const isProbe =
          url.includes("/health") ||
          url.includes("/status") ||
          url.includes("/ping");

        if (status === 401 && !isProbe) {
          if (typeof onLogout === "function") onLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [onLogout]);

  const fetchLockDashboardSummary = useCallback(async ({ silent = true } = {}) => {
    if (accountMode !== "real") {
      setLockDashboardContracts([]);
      setLockDashboardUpdatedAt(null);
      return [];
    }

    if (!silent) setLockDashboardLoading(true);

    try {
      const response = await axios.get(`${getLockApiBase()}/list.php`, {
        timeout: 8000,
      });

      const rows = Array.isArray(response?.data?.contracts)
        ? response.data.contracts
        : [];

      setLockDashboardContracts(rows);
      setLockDashboardUpdatedAt(new Date());
      return rows;
    } catch (error) {
      console.error("[TRADER LOCK SUMMARY]", error?.response?.data || error);
      return [];
    } finally {
      if (!silent) setLockDashboardLoading(false);
    }
  }, [accountMode]);

  useEffect(() => {
    if (accountMode !== "real") {
      setLockDashboardContracts([]);
      return undefined;
    }

    fetchLockDashboardSummary({ silent: true });

    const id = setInterval(() => {
      fetchLockDashboardSummary({ silent: true });
    }, 15000);

    return () => clearInterval(id);
  }, [accountMode, fetchLockDashboardSummary]);


  // ===== RESOLVE API BASE =====
  const resolveApiBase = useCallback(async () => {
    const origins = buildOriginCandidates();

    const apiCandidates = [];
    for (const o of origins) {
      apiCandidates.push(`${o}/api/trader`);
      apiCandidates.push(`${o}/api/trader/index.php`);
    }

    const savedApi = localStorage.getItem("api_base_used");
    if (savedApi) apiCandidates.unshift(savedApi);

    let foundApi = "";
    for (const c of apiCandidates) {
      const ok = await probeApiBase(c);
      if (ok) {
        foundApi = c;
        break;
      }
    }

    if (foundApi) {
      localStorage.setItem("api_base_used", foundApi);
      setApiBase(foundApi);
    }
  }, []);

  useEffect(() => {
    resolveApiBase();
  }, [resolveApiBase]);

  // ===== FETCHERS (API trader) =====
  const fetchBalance = useCallback(async () => {
    if (!apiBase) return;
    try {
      const r = await axios.get(`${apiBase}/user/balance`, { timeout: 5000 });
      const data = r?.data ?? {};

      const bal = Number(data.balance ?? data.balance_brl ?? data.data?.balance ?? data.data?.balance_brl);
      const qty = Number(
        data.total_cofy_qty ??
          data.total_lots ??
          data.total_qty ??
          data.lots ??
          data.cofy_qty ??
          data.data?.total_cofy_qty ??
          data.data?.total_lots
      );

      setBalance(Number.isFinite(bal) ? bal : 0);
      setTotalCofyQty(Number.isFinite(qty) ? qty : 0);
      setBalanceLoaded(true);
    } catch (e) {}
  }, [apiBase]);

  const fetchDemoAccount = useCallback(async (silent = false) => {
    if (!apiBase) return null;
    if (!silent) setDemoLoading(true);
    if (!silent) setDemoError("");
    try {
      const r = await axios.get(`${apiBase}/demo/account`, { timeout: 5000 });
      const data = r?.data ?? {};
      const bal = Number(data.balance ?? data.balance_brl ?? data.initial_balance ?? 10000);
      const safeBalance = Number.isFinite(bal) ? bal : 10000;
      setDemoBalance(safeBalance);
      return data;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Não foi possível carregar a conta Demo.";
      setDemoError(msg);
      return null;
    } finally {
      if (!silent) setDemoLoading(false);
    }
  }, [apiBase]);

  const fetchDemoOrders = useCallback(async () => {
    if (!apiBase) return [];
    try {
      const r = await axios.get(`${apiBase}/demo/orders`, { timeout: 5000 });
      const list = Array.isArray(r?.data?.orders) ? r.data.orders : [];
      const normalized = list.map((o) => ({
        ...o,
        entry_price_saca: o.entry_price_saca ?? o.entry_price,
        saca_entry_price: o.saca_entry_price ?? o.entry_price,
        saca_qty: o.saca_qty ?? o.cofy_qty ?? o.qty,
        qty_saca: o.qty_saca ?? o.cofy_qty ?? o.qty,
        amount: o.amount,
        profit_loss: o.profit_loss,
      }));
      const demoOnly = normalized.map((o) => ({ ...o, _cofy_account_mode: "demo" }));
      setDemoOrders(demoOnly);
      return normalized;
    } catch (_) {
      return [];
    }
  }, [apiBase]);

  useEffect(() => {
    if (accountMode === "demo" && apiBase) {
      fetchDemoAccount();
      fetchDemoOrders();
    }
  }, [accountMode, apiBase, fetchDemoAccount, fetchDemoOrders]);

  async function switchAccountMode(mode) {
    // Fecha painéis da conta anterior antes de trocar a origem das ordens.
    setShowHistoryPanel(false);
    setShowTradePanel(false);
    setPendingTradeConfirm(null);
    setTradeError("");
    try {
      localStorage.setItem("cofy_trader_account_mode", mode === "demo" ? "demo" : "real");
    } catch (_) {}

    if (mode === "demo") {
      setAccountMode("demo");
      setTradeAmount("");
      setTradeError("");
      setPendingTradeConfirm(null);
      await Promise.all([fetchDemoAccount(), fetchDemoOrders()]);
      return;
    }

    setAccountMode("real");
    setTradeAmount("");
    setTradeError("");
    setPendingTradeConfirm(null);
    fetchBalance();
    fetchOrders();
  }

  const fetchUserProfile = useCallback(async () => {
    if (!apiBase) return;

    setProfileLoading(true);
    try {
      const r = await axios.get(`${apiBase}/user/profile`, { timeout: 5000 });
      const data = r?.data ?? {};
      const minutes = Number(data.min_close_time_minutes ?? data.data?.min_close_time_minutes);
      const spread = Number(data.spread_percentage ?? data.data?.spread_percentage);

      setUserSettings({
        min_close_time_minutes: Number.isFinite(minutes) ? minutes : 1440,
        spread_percentage: Number.isFinite(spread) ? spread : 0.2,
      });

      setWithdrawProfileCompleted(Boolean(data.withdraw_profile_completed));

      setProfileData((prev) => ({
        ...prev,
        name: data.name ?? prev.name ?? user?.name ?? "",
        email: data.email ?? prev.email ?? user?.email ?? "",
        cpf: data.cpf ?? "",
        phone: data.phone ?? "",
        birth_date: data.birth_date ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        pix_key_type: data.pix_key_type ?? "cpf",
        pix_key: data.pix_key ?? "",
      }));

      return data;
    } catch (e) {
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, [apiBase, user]);

  const fetchUserNotices = useCallback(async () => {
    if (!apiBase) return;

    setNoticeLoading(true);
    try {
      const r = await axios.get(`${apiBase}/user/messages`, { timeout: 5000 });
      const rows = Array.isArray(r?.data?.rows) ? r.data.rows : [];
      setUserNotices(rows);
    } catch (e) {
      // Aviso não deve impedir o uso do Trader se houver falha temporária.
      console.error("[TRADER NOTICE] erro ao carregar:", e?.response?.data || e);
    } finally {
      setNoticeLoading(false);
    }
  }, [apiBase]);

  const handleNoticeRead = async () => {
    const current = userNotices[0];
    if (!current?.id || !apiBase || noticeConfirming) return;

    setNoticeConfirming(true);
    try {
      await axios.post(
        `${apiBase}/user/messages/${current.id}/read`,
        {},
        { timeout: 5000 }
      );

      // Remove imediatamente da tela.
      setUserNotices((prev) => prev.filter((item) => item.id !== current.id));
    } catch (e) {
      console.error("[TRADER NOTICE] erro ao confirmar leitura:", e?.response?.data || e);
    } finally {
      setNoticeConfirming(false);
    }
  };

  const fetchPartnerData = useCallback(async () => {
    if (!apiBase) return;

    setPartnerLoading(true);
    try {
      const r = await axios.get(`${apiBase}/partner/me`, {
        timeout: 5000,
        validateStatus: () => true,
      });

      // Conta comum não deve sofrer nenhum impacto.
      if (r?.status === 200 && r?.data?.is_partner === true) {
        const data = r.data;
        setPartnerData(data);

        // Badge de novas comissões.
        // Na primeira vez, considera o histórico atual como já visto para não
        // mostrar dezenas de notificações antigas.
        const partnerId = Number(data?.partner?.id || 0);
        const commissions = Array.isArray(data?.commissions) ? data.commissions : [];
        const maxCommissionId = commissions.reduce(
          (max, item) => Math.max(max, Number(item?.id || 0)),
          0
        );

        if (partnerId > 0) {
          const storageKey = `cofy_partner_seen_commission_${partnerId}`;
          const stored = localStorage.getItem(storageKey);

          if (stored === null) {
            localStorage.setItem(storageKey, String(maxCommissionId));
            setNewPartnerCommissions(0);
          } else {
            const lastSeenId = Number(stored || 0);
            const unread = commissions.filter(
              (item) => Number(item?.id || 0) > lastSeenId
            ).length;
            setNewPartnerCommissions(unread);
          }
        }
      } else {
        setPartnerData(null);
        setNewPartnerCommissions(0);
      }
    } catch (e) {
      console.error("[PARTNER] erro ao carregar:", e?.response?.data || e);
      setPartnerData(null);
    } finally {
      setPartnerLoading(false);
    }
  }, [apiBase]);

  const fetchOrders = useCallback(async () => {
    if (!apiBase) return;
    try {
      const r = await axios.get(`${apiBase}/trading/orders`, { timeout: 5000 });
      const data = r?.data ?? {};

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.orders)
        ? data.orders
        : Array.isArray(data.data?.orders)
        ? data.data.orders
        : [];

      const realOnly = list.map((o) => ({ ...o, _cofy_account_mode: "real" }));
      setOrders(realOnly);
    } catch (e) {}
  }, [apiBase]);

  // ===== GRÁFICO PROFISSIONAL - histórico oficial COFY =====
  // O frontend pode estar em localhost, mas o histórico fica no servidor oficial.
  const CHART_HISTORY_URL = "https://cofy.app/api/trader/market/history";

  const fetchChartCandles = useCallback(async () => {
    try {
      const resp = await quotesAxios.get(CHART_HISTORY_URL, {
        timeout: 8000,
        validateStatus: () => true,
        params: { instrument: selectedInstrument, _: Date.now() },
      });

      if (resp?.status !== 200 || resp?.data?.ok !== true) {
        throw new Error(resp?.data?.detail || `HTTP ${resp?.status || 0}`);
      }

      const rows = Array.isArray(resp?.data?.candles) ? resp.data.candles : [];
      const valid = rows.filter(
        (c) =>
          toNumber(c?.o, 0) > 0 &&
          toNumber(c?.h, 0) > 0 &&
          toNumber(c?.l, 0) > 0 &&
          toNumber(c?.c, 0) > 0
      );

      if (!valid.length) throw new Error("Histórico sem candles válidos");

      setChartCandles(valid.slice(-600));
      setChartError("");
    } catch (error) {
      console.error("[TRADER CHART API] erro:", error);
      setChartError("Não foi possível carregar o histórico do COFY.");
    }
  }, [selectedInstrument]);

  useEffect(() => {
    fetchChartCandles();
    const id = setInterval(fetchChartCandles, 10000);
    return () => clearInterval(id);
  }, [fetchChartCandles]);

  // ===== FETCH TRADER QUOTES (RAW) =====
  const fetchTraderQuotes = useCallback(async () => {
    try {
      let base = quotesBase || "";

      // resolve base se ainda não tem ou se mudou ambiente
      if (!base) {
        base = await resolveQuotesBase();
        if (base) {
          localStorage.setItem("quotes_base_used", base);
          setQuotesBase(base);
        }
      }

      if (!base) {
        // warn (1x a cada 5s)
        const now = Date.now();
        if (now - lastWarnAtRef.current > 5000) {
          lastWarnAtRef.current = now;
          console.warn("[TRADER QUOTES] não achei base válida. Verifique /data ou /api/data no mesmo domínio.");
        }
        return;
      }

      const [kcResp, rcResp, dolResp] = await Promise.all([
        quotesAxios.get(join(base, "/oncafe_kc.json"), { validateStatus: () => true }),
        quotesAxios.get(join(base, "/oncafe_rc.json"), { validateStatus: () => true }),
        quotesAxios.get(join(base, "/oncafe_dol.json"), { validateStatus: () => true }),
      ]);

      if (kcResp?.status !== 200 || dolResp?.status !== 200) {
        // força re-resolve na próxima volta
        localStorage.removeItem("quotes_base_used");
        setQuotesBase("");
        return;
      }

      const kcRaw = kcResp.data || null;
      const rcRaw = rcResp?.status === 200 ? (rcResp.data || null) : null;
      const dolRaw = dolResp.data || null;
      setLastQuoteUpdateAt(Date.now());

      // ----- dólar RAW -----
      const dollar =
        dolRaw && typeof dolRaw === "object"
          ? toNumber(dolRaw.price ?? dolRaw.ask ?? dolRaw.bid, 0)
          : 0;

      if (dollar > 0 && dollar < 20) {
        lastGoodUsdRef.current = dollar;
        setUsdBrl(dollar);
      } else if (lastGoodUsdRef.current > 0) {
        setUsdBrl((prev) => prev || lastGoodUsdRef.current);
      }

      // ----- KC RAW (IGNORA price_adjusted) -----
      if (kcRaw && typeof kcRaw === "object") {
        const sym = String(kcRaw.symbol || DEFAULT_KC_SYMBOL);
        const kcCents = toNumber(kcRaw.price, 0); // ✅ só raw

        if (kcCents > 0) {
          setSelectedKcSymbol(sym);

          lastGoodKcDisplayRef.current = kcCents;
          setKcDisplayCents(kcCents);

          const payload = {
            symbol: sym,
            price: kcCents,
            change_pct: toNumber(kcRaw.change_pct ?? kcRaw.changePct, 0),
            change_abs: toNumber(kcRaw.change_abs ?? kcRaw.changeAbs, 0),
            bid: toNumber(kcRaw.bid, 0),
            ask: toNumber(kcRaw.ask, 0),
            open: toNumber(kcRaw.open ?? kcRaw.abertura, 0),
            high: toNumber(kcRaw.high ?? kcRaw.max ?? kcRaw.maxima ?? kcRaw.maximum, 0),
            low: toNumber(kcRaw.low ?? kcRaw.min ?? kcRaw.minima ?? kcRaw.minimum, 0),
            close: toNumber(
              kcRaw.close ??
                kcRaw.previous_close ??
                kcRaw.prev_close ??
                kcRaw.fechamento ??
                kcRaw.settle ??
                kcRaw.settlement,
              0
            ),
          };

          lastGoodKcRef.current = payload;
          setKcData(payload);
        } else if (lastGoodKcRef.current) {
          setKcData((prev) => prev || lastGoodKcRef.current);
        }
      } else if (lastGoodKcRef.current) {
        setKcData((prev) => prev || lastGoodKcRef.current);
      }

      if (kcDisplayCents <= 0 && lastGoodKcDisplayRef.current > 0) {
        setKcDisplayCents((prev) => prev || lastGoodKcDisplayRef.current);
      }

      // ----- RC RAW (Conilon/Robusta Londres, US$/t) -----
      if (rcRaw && typeof rcRaw === "object") {
        const rcPrice = toNumber(rcRaw.price, 0);
        if (rcPrice > 0) {
          const payload = {
            symbol: String(rcRaw.symbol || "RCX26"),
            price: rcPrice,
            change_pct: toNumber(rcRaw.change_pct ?? rcRaw.changePct, 0),
            change_abs: toNumber(rcRaw.change_abs ?? rcRaw.changeAbs, 0),
            bid: toNumber(rcRaw.bid, 0),
            ask: toNumber(rcRaw.ask, 0),
            open: toNumber(rcRaw.open, 0),
            high: toNumber(rcRaw.high, 0),
            low: toNumber(rcRaw.low, 0),
          };
          lastGoodRcRef.current = payload;
          setRcData(payload);
        }
      } else if (lastGoodRcRef.current) {
        setRcData((prev) => prev || lastGoodRcRef.current);
      }

      // log 1x quando mudar a base
      if (base && lastQuotesBaseRef.current !== base) {
        lastQuotesBaseRef.current = base;
        console.log("[TRADER QUOTES] base:", base);
      }

      setMarketData((prev) => ({
        ...(prev || {}),
        symbol: selectedInstrument,
        raw: { kc: kcRaw, rc: rcRaw, dol: dolRaw, base },
      }));
    } catch (e) {
      // força re-resolve
      localStorage.removeItem("quotes_base_used");
      setQuotesBase("");
    }
  }, [quotesBase, kcDisplayCents]);

  // ✅ cotações SEMPRE (não dependem do apiBase)
  useEffect(() => {
    fetchTraderQuotes();
    const id = setInterval(fetchTraderQuotes, 1000);
    return () => clearInterval(id);
  }, [fetchTraderQuotes]);

  // ✅ operações dependem do apiBase
  useEffect(() => {
    if (!apiBase) return;

    fetchBalance();
    fetchUserProfile();
    fetchOrders();
    fetchUserNotices();
    fetchPartnerData();

    const ordersInterval = setInterval(fetchOrders, 1000);
    const balanceInterval = setInterval(fetchBalance, 5000);
    const noticesInterval = setInterval(fetchUserNotices, 30000);
    const partnerInterval = setInterval(fetchPartnerData, 30000);

    return () => {
      clearInterval(ordersInterval);
      clearInterval(balanceInterval);
      clearInterval(noticesInterval);
      clearInterval(partnerInterval);
    };
  }, [apiBase, fetchBalance, fetchUserProfile, fetchOrders, fetchUserNotices, fetchPartnerData]);

  useEffect(() => {
    if (!apiBase || accountMode !== "demo") return;

    // Enquanto o motor Demo ainda é acionado por /demo/orders,
    // consultamos as ordens a cada 1s. Isso NÃO muda o texto do botão.
    fetchDemoOrders();
    fetchDemoAccount(true);

    const ordersId = setInterval(fetchDemoOrders, 1000);
    const balanceId = setInterval(() => fetchDemoAccount(true), 5000);

    return () => {
      clearInterval(ordersId);
      clearInterval(balanceId);
    };
  }, [apiBase, accountMode, fetchDemoOrders, fetchDemoAccount]);

  // ===== derived =====
  const activeBalance = accountMode === "demo" ? demoBalance : balance;
  // REAL e DEMO usam listas totalmente separadas.
  const safeOrders = accountMode === "demo"
    ? (Array.isArray(demoOrders) ? demoOrders : []).filter((o) => o?._cofy_account_mode === "demo")
    : (Array.isArray(orders) ? orders : []).filter((o) => o?._cofy_account_mode === "real");
  const openOrders = safeOrders.filter((o) => String(o?.status || "").toLowerCase() === "open");
  const pendingOrders = safeOrders.filter((o) => String(o?.status || "").toLowerCase() === "pending");
  const closedOrders = safeOrders.filter((o) => ["closed","cancelled","canceled"].includes(String(o?.status || "").toLowerCase()));

  const totalProfit = closedOrders.reduce((sum, o) => {
    const v = Number(o?.profit_loss ?? o?.profit_loss_brl ?? o?.profit ?? o?.pnl);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);

  const spreadPct = toNumber(userSettings?.spread_percentage, 0.2);
  const effectiveUsdBrl = usdBrl > 0 ? usdBrl : 0;

  // Preço de referência da saca conforme o mercado selecionado.
  const arabicaFairPrice = useMemo(() => {
    const kcCentsPerLb = toNumber(kcDisplayCents > 0 ? kcDisplayCents : kcData?.price, 0);
    const dollar = toNumber(effectiveUsdBrl, 0);
    if (kcCentsPerLb <= 0 || dollar <= 0) return 0;
    return (kcCentsPerLb / 100) * LBS_PER_60KG_SACK * dollar;
  }, [kcDisplayCents, kcData, effectiveUsdBrl]);

  const conilonFairPrice = useMemo(() => {
    const rcUsdTon = toNumber(rcData?.price, 0);
    const dollar = toNumber(effectiveUsdBrl, 0);
    if (rcUsdTon <= 0 || dollar <= 0) return 0;

    // Mesmo preço teórico usado pelo backend Trader e pelo COFY LOCK:
    // US$/ton × 0,06 tonelada por saca × USD/BRL.
    return rcUsdTon * 0.06 * dollar;
  }, [rcData, effectiveUsdBrl]);

  const fairPrice = selectedInstrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
  const activeQuoteData = selectedInstrument === "CAFE_CONILON" ? rcData : kcData;
  const activeQuoteValue = selectedInstrument === "CAFE_CONILON"
    ? toNumber(rcData?.price, 0)
    : toNumber(kcDisplayCents > 0 ? kcDisplayCents : kcData?.price, 0);
  const activeSymbol = selectedInstrument === "CAFE_CONILON"
    ? String(rcData?.symbol || "RCX26")
    : selectedKcSymbol;
  const activeChangePct = toNumber(activeQuoteData?.change_pct, 0);
  const activeQuoteUnit = selectedInstrument === "CAFE_CONILON" ? "US$/t" : "¢/lb";

  const lockDashboardSummary = useMemo(() => {
    const rows = Array.isArray(lockDashboardContracts)
      ? lockDashboardContracts
      : [];

    const activeRows = rows.filter(
      (contract) => String(contract?.status || "").toUpperCase() === "ACTIVE"
    );

    const signedRows = rows.filter(
      (contract) => String(contract?.status || "").toUpperCase() === "SIGNED"
    );

    const priority = activeRows[0] || signedRows[0] || rows[0] || null;
    const live = priority?.live || {};

    const protectedPrice = toNumber(
      priority?.protected_price_brl_per_bag ??
        priority?.initial_price_brl_per_bag,
      0
    );

    const currentPrice =
      live?.current_price_brl_per_bag != null
        ? toNumber(live.current_price_brl_per_bag, 0)
        : 0;

    return {
      total: rows.length,
      activeCount: activeRows.length,
      signedCount: signedRows.length,
      primary: priority,
      live,
      protectedPrice,
      currentPrice,
      variationPct: toNumber(live?.variation_pct, 0),
      protectionAmount: toNumber(live?.protection_amount, 0),
      upsideAmount: toNumber(live?.upside_amount, 0),
      daysRemaining:
        live?.days_remaining != null
          ? Math.max(0, Number(live.days_remaining) || 0)
          : null,
    };
  }, [lockDashboardContracts]);

  const selectInstrument = (instrument) => {
    const normalized = instrument === "CAFE_CONILON" ? "CAFE_CONILON" : "CAFE_ARABICA";
    setSelectedInstrument(normalized);
    localStorage.setItem("cofy_trader_instrument", normalized);
    setTradeError("");
  };

  const buyPrice = fairPrice > 0 ? fairPrice * (1 + spreadPct / 100) : 0;
  const sellPrice = fairPrice > 0 ? fairPrice * (1 - spreadPct / 100) : 0;

  // Resultado flutuante: cada posição usa o preço do seu próprio instrumento.
  const openProfitLoss = useMemo(() => {
    return openOrders.reduce((sum, o) => {
      const instrument = String(o?.instrument || o?.symbol || "CAFE_ARABICA").toUpperCase();
      const orderFair = instrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
      if (orderFair <= 0) return sum;
      const side = String(o?.side || "").toLowerCase();
      const entry = toNumber(o?.entry_price ?? o?.entry_price_saca ?? o?.saca_entry_price, 0);
      const qty = toNumber(o?.cofy_qty ?? o?.qty ?? o?.saca_qty ?? o?.qty_saca, 0);
      const orderSpread = toNumber(o?.spread_percentage, spreadPct);
      if (entry <= 0 || qty <= 0) return sum;
      const executableClose = side === "buy"
        ? orderFair * (1 - orderSpread / 100)
        : orderFair * (1 + orderSpread / 100);
      const pnl = side === "buy" ? qty * (executableClose - entry) : qty * (entry - executableClose);
      return sum + (Number.isFinite(pnl) ? pnl : 0);
    }, 0);
  }, [openOrders, arabicaFairPrice, conilonFairPrice, spreadPct]);

  const pendingReservedTotal = useMemo(
    () => pendingOrders.reduce((sum, o) => sum + Math.max(0, toNumber(o?.amount, 0)), 0),
    [pendingOrders]
  );

  const openInvestedTotal = useMemo(
    () => openOrders.reduce((sum, o) => sum + Math.max(0, toNumber(o?.amount, 0)), 0),
    [openOrders]
  );

  const tradeAmountNum = Number(tradeAmount) || 0;
  const estimatedBuyCofy = buyPrice > 0 && tradeAmountNum > 0 ? tradeAmountNum / buyPrice : 0;
  const estimatedSellCofy = sellPrice > 0 && tradeAmountNum > 0 ? tradeAmountNum / sellPrice : 0;

  const positionLots = useMemo(() => {
    if (accountMode === "demo") {
      return openOrders.reduce((sum, o) => sum + Math.max(0, toNumber(o?.cofy_qty ?? o?.qty, 0)), 0);
    }
    return totalCofyQty > 0 ? totalCofyQty : 0;
  }, [accountMode, openOrders, totalCofyQty]);
  const positionSacks = useMemo(() => positionLots, [positionLots]); // 1 lote = 1 saca

  const positionValueBrl = useMemo(() => {
    return openOrders.reduce((sum, o) => {
      const instrument = String(o?.instrument || o?.symbol || "CAFE_ARABICA").toUpperCase();
      const orderFair = instrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
      const qty = Math.max(0, toNumber(o?.cofy_qty ?? o?.qty, 0));
      return sum + (orderFair > 0 ? qty * orderFair : 0);
    }, 0);
  }, [openOrders, arabicaFairPrice, conilonFairPrice]);

  const cashSacksAtBuy = useMemo(() => {
    if (buyPrice <= 0 || activeBalance <= 0) return 0;
    return activeBalance / buyPrice;
  }, [activeBalance, buyPrice]);

  const portfolioTotalBrl = useMemo(() => {
    const b = Number.isFinite(Number(activeBalance)) ? Number(activeBalance) : 0;
    const p = Number.isFinite(Number(positionValueBrl)) ? Number(positionValueBrl) : 0;
    return Math.max(0, b) + Math.max(0, p);
  }, [activeBalance, positionValueBrl]);

  const portfolioSacksAtBuy = useMemo(() => {
    if (buyPrice <= 0 || portfolioTotalBrl <= 0) return 0;
    return portfolioTotalBrl / buyPrice;
  }, [portfolioTotalBrl, buyPrice]);

  const userSettingsNoMinClose = { ...userSettings, min_close_time_minutes: 0 };

  const marketDataForOrders = useMemo(() => {
    if (fairPrice <= 0) return marketData;
    return {
      ...(marketData || {}),
      symbol: "SACA_CAFE_BRL",
      price: fairPrice,
      currentPrice: fairPrice,
      current_price: fairPrice,
      open: fairPrice,
      high: fairPrice,
      low: fairPrice,
      change_pct: toNumber(activeQuoteData?.change_pct, marketData?.change_pct ?? 0),
      change_abs: toNumber(activeQuoteData?.change_abs, marketData?.change_abs ?? 0),
    };
  }, [marketData, fairPrice, activeQuoteData, selectedInstrument]);

  // ===== actions =====
  const handleOrderPlaced = () => {
    if (accountMode === "demo") {
      fetchDemoAccount();
      fetchDemoOrders();
    } else {
      fetchBalance();
      fetchOrders();
    }
  };

  const resetTradeFormAfterSuccess = () => {
    setTradeAmount("");
    setTriggerPrice("");
    setStopLossEnabled(false);
    setStopLossPrice("");
    setTakeProfitEnabled(false);
    setTakeProfitPrice("");
    setPendingTradeConfirm(null);
    setShowTradePanel(false);
    setTradeError("");
  };

  const parseTradePrice = (value) => {
    const parsed = Number(String(value || "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const handleBuy = async () => {
    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setTradeError(accountMode === "demo" ? "Informe um valor em reais para abrir a COMPRA Demo." : "Informe um valor em reais para abrir operação de COMPRA.");
      return;
    }
    if (amountNum > activeBalance + 0.00001) {
      setTradeError(accountMode === "demo" ? "Saldo Demo insuficiente." : "Saldo em reais insuficiente para abrir essa operação.");
      return;
    }
    if (accountMode === "real" && fairPrice <= 0) {
      setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço.");
      return;
    }

    const trigger = parseTradePrice(triggerPrice);
    const sl = parseTradePrice(stopLossPrice);
    const tp = parseTradePrice(takeProfitPrice);

    setBuyLoading(true);
    setTradeError("");
    try {
      if (accountMode === "demo") {
        const r = await axios.post(`${apiBase}/demo/orders`, {
          side: "buy",
          instrument: selectedInstrument,
          amount: amountNum,
          order_type: orderType,
          ...(["limit", "stop"].includes(orderType) ? { trigger_price: trigger } : {}),
          ...(stopLossEnabled && sl > 0 ? { stop_loss: sl } : {}),
          ...(takeProfitEnabled && tp > 0 ? { take_profit: tp } : {}),
        });
        if (Number.isFinite(Number(r?.data?.balance))) setDemoBalance(Number(r.data.balance));
        await fetchDemoOrders();
      } else {
        await axios.post(`${apiBase}/trading/orders`, {
          side: "buy",
          instrument: selectedInstrument,
          amount: amountNum,
          order_type: orderType,
          ...(["limit", "stop"].includes(orderType) ? { trigger_price: trigger } : {}),
          ...(stopLossEnabled && sl > 0 ? { stop_loss: sl } : {}),
          ...(takeProfitEnabled && tp > 0 ? { take_profit: tp } : {}),
        });
        handleOrderPlaced();
      }

      resetTradeFormAfterSuccess();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.error ||
        (accountMode === "demo" ? "Erro ao abrir COMPRA Demo." : "Erro ao abrir operação de compra.");
      setTradeError(msg);
      setPendingTradeConfirm(null);
    } finally {
      setBuyLoading(false);
    }
  };

  const handleSell = async () => {
    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setTradeError(accountMode === "demo" ? "Informe um valor em reais para abrir a VENDA Demo." : "Informe um valor em reais para abrir operação de VENDA.");
      return;
    }
    if (amountNum > activeBalance + 0.00001) {
      setTradeError(accountMode === "demo" ? "Saldo Demo insuficiente." : "Saldo em reais insuficiente para abrir essa operação.");
      return;
    }
    if (accountMode === "real" && fairPrice <= 0) {
      setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço.");
      return;
    }

    const trigger = parseTradePrice(triggerPrice);
    const sl = parseTradePrice(stopLossPrice);
    const tp = parseTradePrice(takeProfitPrice);

    setSellLoading(true);
    setTradeError("");
    try {
      if (accountMode === "demo") {
        const r = await axios.post(`${apiBase}/demo/orders`, {
          side: "sell",
          instrument: selectedInstrument,
          amount: amountNum,
          order_type: orderType,
          ...(["limit", "stop"].includes(orderType) ? { trigger_price: trigger } : {}),
          ...(stopLossEnabled && sl > 0 ? { stop_loss: sl } : {}),
          ...(takeProfitEnabled && tp > 0 ? { take_profit: tp } : {}),
        });
        if (Number.isFinite(Number(r?.data?.balance))) setDemoBalance(Number(r.data.balance));
        await fetchDemoOrders();
      } else {
        await axios.post(`${apiBase}/trading/orders`, {
          side: "sell",
          instrument: selectedInstrument,
          amount: amountNum,
          order_type: orderType,
          ...(["limit", "stop"].includes(orderType) ? { trigger_price: trigger } : {}),
          ...(stopLossEnabled && sl > 0 ? { stop_loss: sl } : {}),
          ...(takeProfitEnabled && tp > 0 ? { take_profit: tp } : {}),
        });
        handleOrderPlaced();
      }

      resetTradeFormAfterSuccess();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.error ||
        (accountMode === "demo" ? "Erro ao abrir VENDA Demo." : "Erro ao abrir operação de venda.");
      setTradeError(msg);
      setPendingTradeConfirm(null);
    } finally {
      setSellLoading(false);
    }
  };

  const closeDemoOrder = async (id) => {
    if (!apiBase || !id || demoClosingId) return;
    setDemoClosingId(id);
    try {
      const r = await axios.post(`${apiBase}/demo/orders/${id}/close`, {});
      if (Number.isFinite(Number(r?.data?.balance))) setDemoBalance(Number(r.data.balance));
      await fetchDemoOrders();
    } catch (error) {
      setTradeError(error?.response?.data?.detail || "Não foi possível encerrar a operação Demo.");
    } finally {
      setDemoClosingId(null);
    }
  };

  const cancelPendingOrder = async (id) => {
    if (!apiBase || !id || cancelingPendingId) return;
    setCancelingPendingId(id);
    setTradeError("");
    try {
      if (Number(pendingEditOrder?.id) === Number(id)) {
        setPendingEditOrder(null);
      }
      if (accountMode === "demo") {
        const r = await axios.post(`${apiBase}/demo/orders/${id}/cancel`, {});
        if (Number.isFinite(Number(r?.data?.balance))) setDemoBalance(Number(r.data.balance));
        await Promise.all([fetchDemoOrders(), fetchDemoAccount(true)]);
      } else {
        await axios.post(`${apiBase}/trading/orders/${id}/cancel`, {});
        await Promise.all([fetchOrders(), fetchBalance()]);
      }
    } catch (error) {
      setTradeError(error?.response?.data?.detail || "Não foi possível cancelar a ordem pendente.");
    } finally {
      setCancelingPendingId(null);
    }
  };

  const openOpenOrderManager = (order) => {
    if (!order?.id) return;
    setOpenManageOrder(order);
    setOpenManageStopLoss(order?.stop_loss != null ? String(order.stop_loss) : "");
    setOpenManageTakeProfit(order?.take_profit != null ? String(order.take_profit) : "");
    setOpenManageError("");
  };

  const closeOpenOrderManager = () => {
    if (openManageSaving) return;
    setOpenManageOrder(null);
    setOpenManageStopLoss("");
    setOpenManageTakeProfit("");
    setOpenManageError("");
  };

  const saveOpenOrderProtection = async () => {
    if (!apiBase || !openManageOrder?.id || openManageSaving) return;

    const parseOptional = (value) => {
      const raw = String(value ?? "").trim().replace(",", ".");
      if (raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : NaN;
    };

    const stopLoss = parseOptional(openManageStopLoss);
    const takeProfit = parseOptional(openManageTakeProfit);

    if (Number.isNaN(stopLoss) || Number.isNaN(takeProfit)) {
      setOpenManageError("Stop Loss ou Take Profit inválido.");
      return;
    }

    setOpenManageSaving(true);
    setOpenManageError("");

    try {
      const protectionUrl = accountMode === "demo"
        ? `${apiBase}/demo/orders/${openManageOrder.id}/protection`
        : `${apiBase}/trading/orders/${openManageOrder.id}/protection`;

      await axios.post(
        protectionUrl,
        {
          stop_loss: stopLoss,
          take_profit: takeProfit,
        },
        { timeout: 10000 }
      );

      if (accountMode === "demo") {
        await Promise.all([fetchDemoOrders(), fetchDemoAccount(true)]);
      } else {
        await Promise.all([fetchOrders(), fetchBalance()]);
      }

      setOpenManageOrder(null);
      setOpenManageStopLoss("");
      setOpenManageTakeProfit("");
    } catch (error) {
      setOpenManageError(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Não foi possível salvar Stop Loss / Take Profit."
      );
    } finally {
      setOpenManageSaving(false);
    }
  };

  const openPendingOrderEditor = (order) => {
    if (!order?.id) return;
    const type = String(order?.order_type || "limit").toLowerCase();
    setPendingEditOrder(order);
    setPendingEditType(["limit", "stop"].includes(type) ? type : "limit");
    setPendingEditTrigger(order?.trigger_price != null ? String(order.trigger_price) : "");
    setPendingEditStopLoss(order?.stop_loss != null ? String(order.stop_loss) : "");
    setPendingEditTakeProfit(order?.take_profit != null ? String(order.take_profit) : "");
    setPendingEditError("");
  };

  const closePendingOrderEditor = () => {
    if (pendingEditSaving) return;
    setPendingEditOrder(null);
    setPendingEditError("");
  };

  const savePendingOrderEdit = async () => {
    if (!apiBase || !pendingEditOrder?.id || pendingEditSaving) return;

    const parseOptional = (value) => {
      const raw = String(value ?? "").trim().replace(",", ".");
      if (raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : NaN;
    };

    const trigger = parseOptional(pendingEditTrigger);
    const stopLoss = parseOptional(pendingEditStopLoss);
    const takeProfit = parseOptional(pendingEditTakeProfit);

    if (!Number.isFinite(trigger) || trigger <= 0) {
      setPendingEditError("Informe um preço de gatilho válido.");
      return;
    }
    if (Number.isNaN(stopLoss) || Number.isNaN(takeProfit)) {
      setPendingEditError("Stop Loss ou Take Profit inválido.");
      return;
    }

    const side = String(pendingEditOrder?.side || "").toLowerCase();
    if (side === "buy") {
      if (stopLoss !== null && stopLoss >= trigger) {
        setPendingEditError("Na COMPRA pendente, o Stop Loss deve ficar abaixo do preço de gatilho.");
        return;
      }
      if (takeProfit !== null && takeProfit <= trigger) {
        setPendingEditError("Na COMPRA pendente, o Take Profit deve ficar acima do preço de gatilho.");
        return;
      }
    } else if (side === "sell") {
      if (stopLoss !== null && stopLoss <= trigger) {
        setPendingEditError("Na VENDA pendente, o Stop Loss deve ficar acima do preço de gatilho.");
        return;
      }
      if (takeProfit !== null && takeProfit >= trigger) {
        setPendingEditError("Na VENDA pendente, o Take Profit deve ficar abaixo do preço de gatilho.");
        return;
      }
    }

    setPendingEditSaving(true);
    setPendingEditError("");

    try {
      // apiBase pode ser /api/trader ou /api/trader/index.php.
      // O endpoint isolado evita alterar as rotas existentes do Trader.
      const traderRoot = String(apiBase || "")
        .replace(/\/index\.php\/?$/i, "")
        .replace(/\/+$/, "");

      const response = await axios.post(
        `${traderRoot}/pending_update.php`,
        {
          mode: accountMode === "demo" ? "demo" : "real",
          order_id: Number(pendingEditOrder.id),
          order_type: pendingEditType,
          trigger_price: trigger,
          stop_loss: stopLoss,
          take_profit: takeProfit,
        },
        { timeout: 10000 }
      );

      if (response?.data?.ok !== true) {
        throw new Error(response?.data?.detail || response?.data?.error || "Não foi possível atualizar a ordem.");
      }

      if (accountMode === "demo") {
        await Promise.all([fetchDemoOrders(), fetchDemoAccount(true)]);
      } else {
        await Promise.all([fetchOrders(), fetchBalance()]);
      }

      setPendingEditOrder(null);
    } catch (error) {
      setPendingEditError(
        error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Não foi possível atualizar a ordem pendente."
      );
    } finally {
      setPendingEditSaving(false);
    }
  };

  const formatCpfInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  };

  const formatPhoneInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }

    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2");
  };

  const handleOpenWithdrawal = async () => {
    setShowMenu(false);
    setProfileError("");

    const profile = await fetchUserProfile();

    if (profile?.withdraw_profile_completed) {
      setShowWithdrawal(true);
      return;
    }

    setShowProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (!apiBase || profileSaving) return;

    setProfileSaving(true);
    setProfileError("");

    try {
      const payload = {
        name: String(profileData.name || "").trim(),
        cpf: String(profileData.cpf || ""),
        phone: String(profileData.phone || ""),
        birth_date: String(profileData.birth_date || ""),
        city: String(profileData.city || "").trim(),
        state: String(profileData.state || "").trim().toUpperCase(),
        pix_key_type: String(profileData.pix_key_type || "cpf"),
        pix_key:
          profileData.pix_key_type === "cpf"
            ? String(profileData.cpf || "")
            : String(profileData.pix_key || "").trim(),
      };

      const r = await axios.put(`${apiBase}/user/profile`, payload, { timeout: 12000 });
      const data = r?.data ?? {};

      if (!data?.ok) {
        throw new Error(data?.detail || "Não foi possível salvar o cadastro.");
      }

      setWithdrawProfileCompleted(true);
      setShowProfileModal(false);

      await fetchUserProfile();

      // Depois de completar o cadastro, continua exatamente para o saque.
      setShowWithdrawal(true);
    } catch (error) {
      setProfileError(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Não foi possível salvar o cadastro."
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDepositSuccess = () => {
    setShowDeposit(false);
    fetchBalance();
  };

  const handleWithdrawalSuccess = () => {
    setShowWithdrawal(false);
    fetchBalance();
  };

  const handleOrderClosed = () => {
    if (accountMode === "demo") {
      fetchDemoAccount();
      fetchDemoOrders();
    } else {
      fetchBalance();
      fetchOrders();
    }
  };

  const partner = partnerData?.partner ?? partnerData ?? {};
  const partnerSummary = partnerData?.summary ?? partnerData?.totals ?? partnerData ?? {};
  const partnerReferrals = Array.isArray(partnerData?.referrals)
    ? partnerData.referrals
    : Array.isArray(partnerData?.referred_users)
    ? partnerData.referred_users
    : [];
  const partnerCommissions = Array.isArray(partnerData?.commissions)
    ? partnerData.commissions
    : Array.isArray(partnerData?.history)
    ? partnerData.history
    : [];

  const partnerCode = partner.referral_code ?? partnerData?.referral_code ?? "";
  const partnerCommissionPct = toNumber(partner.commission_pct ?? partnerData?.commission_pct, 0);
  const partnerReferralCount = toNumber(
    partnerSummary.referrals_count ?? partnerSummary.referred_count ?? partnerData?.referrals_count,
    partnerReferrals.length
  );
  const partnerAvailable = toNumber(
    partnerSummary.available ?? partnerSummary.available_amount ?? partnerSummary.commission_available,
    0
  );
  const partnerPaid = toNumber(partnerSummary.paid ?? partnerSummary.paid_amount ?? partnerSummary.commission_paid, 0);
  const partnerPending = toNumber(
    partnerSummary.pending ?? partnerSummary.pending_amount ?? partnerSummary.commission_pending,
    0
  );
  const partnerTotalCommission = toNumber(
    partnerSummary.total_commission ?? partnerSummary.commission_total ?? partnerSummary.total,
    partnerAvailable + partnerPaid + partnerPending
  );
  const partnerSpreadGenerated = toNumber(
    partnerSummary.spread_total ?? partnerSummary.total_spread ?? partnerSummary.spread_generated,
    0
  );

  const partnerReferralLink = partnerCode
    ? `${window.location.origin}/trader?ref=${encodeURIComponent(partnerCode)}`
    : "";

  const handleShareReferral = async () => {
    if (!partnerReferralLink) return;

    const sharePayload = {
      title: "COFY Trader",
      text: "Crie sua conta no COFY pelo meu convite.",
      url: partnerReferralLink,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(partnerReferralLink);
        alert("Link de indicação copiado.");
        return;
      }

      window.prompt("Copie seu link de indicação:", partnerReferralLink);
    } catch (e) {
      // Cancelar o compartilhamento não é erro para o usuário.
      if (e?.name !== "AbortError") {
        window.prompt("Copie seu link de indicação:", partnerReferralLink);
      }
    }
  };

  const openPartnerPanel = () => {
    const partnerId = Number(partner?.id || 0);
    const maxCommissionId = partnerCommissions.reduce(
      (max, item) => Math.max(max, Number(item?.id || 0)),
      0
    );

    if (partnerId > 0) {
      localStorage.setItem(
        `cofy_partner_seen_commission_${partnerId}`,
        String(maxCommissionId)
      );
    }

    setNewPartnerCommissions(0);
    setShowPartnerPanel(true);
  };

  // ===== UI =====
  const currentNotice = userNotices.length > 0 ? userNotices[0] : null;

  const kcDisplay = kcDisplayCents > 0 ? kcDisplayCents : toNumber(kcData?.price, 0);
  const kcChangePct = toNumber(kcData?.change_pct, 0);
  const kcChangeAbs = toNumber(kcData?.change_abs, 0);
  const hasPosition = positionSacks > 0.000001;
  const realAccountNeedsFunding =
    accountMode === "real" &&
    balanceLoaded &&
    Number(balance) <= 0 &&
    !hasPosition &&
    openOrders.length === 0 &&
    pendingOrders.length === 0;
  const selectedEntryPrice = tradeSide === "buy" ? buyPrice : sellPrice;
  const selectedEstimatedQty = tradeSide === "buy" ? estimatedBuyCofy : estimatedSellCofy;
  const tradeAmountDisplay = tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "";

  const setQuickTradeAmount = (amount) => {
    const safe = Math.max(0, Math.min(Number(amount) || 0, Number(activeBalance) || 0));
    setTradeAmount(safe > 0 ? safe.toFixed(2) : "");
    setTradeError("");
  };

  const handleTradeAmountMasked = (rawValue) => {
    const masked = moneyMaskFromDigits(rawValue);
    setTradeAmount(masked.numeric);
    setTradeError("");
  };

  const requestTradeConfirmation = (side) => {
    if (["limit", "stop"].includes(orderType)) {
      const trigger = Number(String(triggerPrice || "").replace(",", "."));
      if (!Number.isFinite(trigger) || trigger <= 0) {
        setTradeError(orderType === "limit" ? "Informe o preço limite." : "Informe o preço Stop.");
        return;
      }

      if (orderType === "limit") {
        if (side === "buy" && trigger >= fairPrice) {
          setTradeError("Na COMPRA Limite, coloque um preço abaixo do preço atual.");
          return;
        }
        if (side === "sell" && trigger <= fairPrice) {
          setTradeError("Na VENDA Limite, coloque um preço acima do preço atual.");
          return;
        }
      } else {
        if (side === "buy" && trigger <= fairPrice) {
          setTradeError("Na COMPRA Stop, coloque um preço acima do preço atual.");
          return;
        }
        if (side === "sell" && trigger >= fairPrice) {
          setTradeError("Na VENDA Stop, coloque um preço abaixo do preço atual.");
          return;
        }
      }
    }
    const sl = parseTradePrice(stopLossPrice);
    const tp = parseTradePrice(takeProfitPrice);

    if (stopLossEnabled && sl <= 0) {
      setTradeError("Informe um preço válido para o Stop Loss.");
      return;
    }
    if (takeProfitEnabled && tp <= 0) {
      setTradeError("Informe um preço válido para o Take Profit.");
      return;
    }

    // Proteções coerentes com a direção da posição.
    if (stopLossEnabled && fairPrice > 0) {
      if (side === "buy" && sl >= fairPrice) {
        setTradeError("Na COMPRA, o Stop Loss deve ficar abaixo do preço atual.");
        return;
      }
      if (side === "sell" && sl <= fairPrice) {
        setTradeError("Na VENDA, o Stop Loss deve ficar acima do preço atual.");
        return;
      }
    }
    if (takeProfitEnabled && fairPrice > 0) {
      if (side === "buy" && tp <= fairPrice) {
        setTradeError("Na COMPRA, o Take Profit deve ficar acima do preço atual.");
        return;
      }
      if (side === "sell" && tp >= fairPrice) {
        setTradeError("Na VENDA, o Take Profit deve ficar abaixo do preço atual.");
        return;
      }
    }

    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) { setTradeError("Informe um valor em reais para abrir a operação."); return; }
    if (fairPrice <= 0) { setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço."); return; }
    if (amountNum > activeBalance + 0.00001) { setTradeError("Saldo em reais insuficiente para abrir essa operação."); return; }
    setTradeSide(side);
    setTradeError("");
    setPendingTradeConfirm({
      instrument: selectedInstrument,
      side,
      amount: amountNum,
      orderType,
      triggerPrice: ["limit", "stop"].includes(orderType) ? Number(String(triggerPrice || "").replace(",", ".")) : null,
      stopLoss: stopLossEnabled ? parseTradePrice(stopLossPrice) : null,
      takeProfit: takeProfitEnabled ? parseTradePrice(takeProfitPrice) : null,
    });
  };

  const quoteIsLive = lastQuoteUpdateAt > 0 && Date.now() - lastQuoteUpdateAt < 10000;
  const quoteTimeLabel = lastQuoteUpdateAt ? new Date(lastQuoteUpdateAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";

  return (
    <div
      className={`min-h-screen text-white transition-colors duration-300 ${
        accountMode === "demo"
          ? "bg-[radial-gradient(circle_at_50%_-10%,rgba(245,158,11,.16),transparent_34%),#080b10]"
          : "bg-slate-950"
      }`}
      data-testid="trading-dashboard"
      data-account-mode={accountMode}
    >
      {/* ===== Aviso enviado pelo Admin ===== */}
      {currentNotice && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
          <div
            className={[
              "w-full max-w-md rounded-2xl border bg-slate-950 shadow-2xl overflow-hidden",
              currentNotice.priority === "important"
                ? "border-amber-500/40"
                : "border-emerald-500/30",
            ].join(" ")}
          >
            <div
              className={[
                "px-4 py-3 border-b flex items-center gap-3",
                currentNotice.priority === "important"
                  ? "border-amber-500/20 bg-amber-500/10"
                  : "border-emerald-500/20 bg-emerald-500/10",
              ].join(" ")}
            >
              <div
                className={[
                  "w-9 h-9 rounded-xl flex items-center justify-center",
                  currentNotice.priority === "important"
                    ? "bg-amber-400/20 text-amber-300"
                    : "bg-emerald-400/20 text-emerald-300",
                ].join(" ")}
              >
                <BellRing className="w-5 h-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">
                  Aviso da COFY
                </p>
                <h2 className="text-base font-bold text-white break-words">
                  {currentNotice.title}
                </h2>
              </div>
            </div>

            <div className="p-4">
              <div className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
                {currentNotice.message}
              </div>

              {currentNotice.priority === "important" && (
                <div className="mt-3 inline-flex items-center rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                  Mensagem importante
                </div>
              )}

              <Button
                type="button"
                onClick={handleNoticeRead}
                disabled={noticeConfirming}
                className="mt-5 w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {noticeConfirming ? "Confirmando..." : "Entendi"}
              </Button>

              {userNotices.length > 1 && (
                <p className="mt-2 text-center text-[10px] text-slate-500">
                  Há mais {userNotices.length - 1} aviso(s) aguardando leitura.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`border-b backdrop-blur-sm sticky top-0 z-50 transition-colors duration-300 ${
        accountMode === "demo"
          ? "border-amber-500/30 bg-[#171006]/90"
          : "border-slate-800 bg-slate-900/60"
      }`}>
        <div className="w-full px-3 sm:px-5 lg:px-8 xl:px-10 py-2.5 sm:py-4 flex items-center justify-between relative">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-colors ${
              accountMode === "demo"
                ? "bg-gradient-to-br from-amber-300 to-orange-500"
                : "bg-gradient-to-br from-emerald-400 to-cyan-400"
            }`}>
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-slate-900" />
            </div>
            <div className="flex flex-col">
              <h1
                className="text-base sm:text-xl font-bold text-white leading-tight"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                Trader Café NY
              </h1>
              <p className="max-w-[220px] truncate text-[10px] text-slate-300 sm:max-w-none sm:text-xs">Olá, {user?.name || user?.email}</p>
            </div>
          </div>

          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowMenu((prev) => !prev)}
              className="border-slate-700 text-white bg-slate-900 hover:bg-slate-800"
            >
              <Menu className="w-4 h-4" />
            </Button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg bg-slate-900 border border-slate-700 shadow-lg z-50">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={() => {
                    setShowMenu(false);
                    setShowDeposit(true);
                  }}
                >
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  Depositar
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={() => {
                    setShowMenu(false);
                    setShowCofyLock(true);
                  }}
                >
                  <LockKeyhole className="w-4 h-4 text-cyan-300" />
                  COFY LOCK
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={async () => {
                    setShowMenu(false);
                    setProfileError("");
                    await fetchUserProfile();
                    setShowProfileModal(true);
                  }}
                >
                  <UserRound className="w-4 h-4 text-amber-300" />
                  {withdrawProfileCompleted ? "Meu cadastro" : "Completar cadastro"}
                </button>

                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={handleOpenWithdrawal}
                >
                  <ArrowDownToLine className="w-4 h-4 text-cyan-400" />
                  Sacar
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-slate-800"
                  onClick={() => {
                    setShowMenu(false);
                    onLogout();
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="w-full px-2.5 sm:px-5 lg:px-8 xl:px-10 py-2.5 sm:py-6">
        <div className="space-y-3 sm:space-y-5">
          {/* Identidade inequívoca da conta ativa */}
          {accountMode === "demo" ? (
            <div className="overflow-hidden rounded-2xl border border-amber-400/50 bg-gradient-to-r from-amber-400 via-amber-300 to-orange-400 text-slate-950 shadow-[0_10px_32px_rgba(245,158,11,.18)]">
              <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4 sm:py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-950/90 text-lg">🧪</div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-black uppercase tracking-[0.13em] sm:text-sm">VOCÊ ESTÁ NA CONTA DEMO</p>
                    <p className="mt-0.5 text-[9px] font-semibold leading-tight text-slate-800 sm:text-xs">
                      Dinheiro virtual para testes, aprendizado e simulações. As operações não são reais.
                    </p>
                  </div>
                </div>
                <span className="hidden shrink-0 rounded-full border border-slate-950/20 bg-slate-950/10 px-3 py-1.5 text-[9px] font-black uppercase tracking-wider sm:inline-flex">
                  SEM DINHEIRO REAL
                </span>
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-emerald-950/65 to-slate-900 text-white shadow-lg shadow-black/10">
              <div className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300">✓</div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.13em] text-emerald-300 sm:text-xs">CONTA REAL</p>
                    <p className="text-[8px] text-slate-400 sm:text-[10px]">Operações com saldo real da sua conta COFY.</p>
                  </div>
                </div>
                <span className="hidden rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[8px] font-black text-emerald-300 sm:inline-flex">AMBIENTE REAL</span>
              </div>
            </div>
          )}
          {/* ===== COFY LOCK • resumo dinâmico ===== */}
          {(() => {
            const lock = lockDashboardSummary;
            const contract = lock.primary;
            const rawStatus = String(contract?.status || "").toUpperCase();
            const isActive = rawStatus === "ACTIVE";
            const isSigned = rawStatus === "SIGNED";
            const protectionActive = isActive && lock.protectionAmount > 0;
            const coffeeLabel =
              contract?.coffee_label ||
              (String(contract?.coffee_type || "").toUpperCase() === "CONILON"
                ? "Conilon"
                : "Arábica");

            return (
              <button
                type="button"
                onClick={() => setShowCofyLock(true)}
                className={`group relative w-full overflow-hidden rounded-2xl border text-left shadow-[0_10px_28px_rgba(6,182,212,.08)] transition active:scale-[0.995] ${
                  isActive
                    ? "border-emerald-300/35 bg-[radial-gradient(circle_at_8%_0%,rgba(16,185,129,.18),transparent_42%),linear-gradient(120deg,#06221d,#081522_55%,#081720)] hover:border-emerald-200/60"
                    : "border-cyan-300/35 bg-[radial-gradient(circle_at_8%_0%,rgba(34,211,238,.18),transparent_42%),linear-gradient(120deg,#071b2a,#081522_55%,#081720)] hover:border-cyan-200/60"
                }`}
              >
                <div className="pointer-events-none absolute -right-10 -bottom-16 h-32 w-32 rounded-full bg-emerald-400/10 blur-3xl" />

                <div className="relative flex items-center gap-3 px-3 py-3 sm:px-4 sm:py-3.5">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border sm:h-11 sm:w-11 ${
                      isActive
                        ? "border-emerald-200/25 bg-emerald-300/10 text-emerald-200"
                        : "border-cyan-200/25 bg-cyan-300/10 text-cyan-200"
                    }`}
                  >
                    {isActive ? (
                      <ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6" />
                    ) : (
                      <LockKeyhole className="h-5 w-5 sm:h-6 sm:w-6" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[7px] font-black uppercase tracking-[0.16em] sm:text-[8px] ${
                          isActive
                            ? "border-emerald-200/20 bg-emerald-300/10 text-emerald-200"
                            : "border-cyan-200/20 bg-cyan-300/10 text-cyan-200"
                        }`}
                      >
                        COFY LOCK
                      </span>

                      {isActive ? (
                        <span className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2 py-0.5 text-[7px] font-black uppercase text-emerald-300 sm:text-[8px]">
                          PROTEGIDO
                        </span>
                      ) : isSigned ? (
                        <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-0.5 text-[7px] font-black uppercase text-amber-300 sm:text-[8px]">
                          AGUARDANDO HEDGE
                        </span>
                      ) : (
                        <span className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2 py-0.5 text-[7px] font-black uppercase text-emerald-300 sm:text-[8px]">
                          LOCK 90
                        </span>
                      )}
                    </div>

                    {isActive ? (
                      <>
                        <p className="mt-1 text-[13px] font-black leading-tight text-white sm:text-base">
                          {protectionActive
                            ? `Proteção estimada ${formatBrl(lock.protectionAmount)}`
                            : `Valorização acima do piso +${formatBrl(lock.upsideAmount)}`}
                        </p>

                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[8px] font-semibold text-slate-400 sm:text-[10px]">
                          <span>{coffeeLabel}</span>
                          <span>•</span>
                          <span>
                            Piso {lock.protectedPrice > 0 ? formatBrl(lock.protectedPrice) : "--"}
                          </span>
                          <span>→</span>
                          <span
                            className={
                              lock.currentPrice >= lock.protectedPrice
                                ? "text-emerald-300"
                                : "text-cyan-300"
                            }
                          >
                            Atual {lock.currentPrice > 0 ? formatBrl(lock.currentPrice) : "--"}
                          </span>
                          {lock.daysRemaining != null && (
                            <>
                              <span>•</span>
                              <span className="inline-flex items-center gap-1">
                                <Clock3 className="h-3 w-3" />
                                {lock.daysRemaining}d
                              </span>
                            </>
                          )}
                        </div>
                      </>
                    ) : isSigned ? (
                      <>
                        <p className="mt-1 text-[13px] font-black leading-tight text-white sm:text-base">
                          Contrato assinado • aguardando confirmação do hedge
                        </p>
                        <p className="mt-0.5 truncate text-[8px] font-semibold text-slate-400 sm:text-[10px]">
                          {coffeeLabel} • {contract?.contract_number || "COFY LOCK"} • ativação após hedge confirmado
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="mt-1 text-[13px] font-black leading-tight text-white sm:text-base">
                          Proteja seu café contra queda de preço
                        </p>
                        <p className="mt-0.5 truncate text-[8px] font-semibold text-slate-400 sm:text-[10px]">
                          Arábica + Conilon • piso protegido • participação na alta
                        </p>
                      </>
                    )}
                  </div>

                  {lockDashboardLoading ? (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900/70 text-slate-400">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    </span>
                  ) : (
                    <>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border text-base font-black transition sm:hidden ${
                          isActive
                            ? "border-emerald-200/25 bg-emerald-300/10 text-emerald-200 group-hover:bg-emerald-300 group-hover:text-slate-950"
                            : "border-cyan-200/25 bg-cyan-300/10 text-cyan-200 group-hover:bg-cyan-300 group-hover:text-slate-950"
                        }`}
                      >
                        →
                      </span>

                      <span
                        className={`hidden h-10 shrink-0 items-center justify-center rounded-xl px-4 text-[9px] font-black text-slate-950 transition sm:inline-flex ${
                          isActive
                            ? "bg-emerald-300 group-hover:bg-emerald-200"
                            : "bg-cyan-300 group-hover:bg-cyan-200"
                        }`}
                      >
                        {isActive || isSigned ? "ACOMPANHAR LOCK →" : "CONHECER LOCK 90 →"}
                      </span>
                    </>
                  )}
                </div>

                {isActive && (
                  <div className="relative grid grid-cols-3 border-t border-emerald-400/10 bg-slate-950/20 text-center">
                    <div className="px-2 py-1.5">
                      <span className="block text-[7px] uppercase text-slate-600">Variação</span>
                      <strong
                        className={`text-[9px] ${
                          lock.variationPct >= 0 ? "text-emerald-300" : "text-red-300"
                        }`}
                      >
                        {lock.variationPct >= 0 ? "+" : ""}
                        {lock.variationPct.toFixed(2)}%
                      </strong>
                    </div>

                    <div className="border-x border-slate-800/70 px-2 py-1.5">
                      <span className="block text-[7px] uppercase text-slate-600">Valor protegido</span>
                      <strong className="text-[9px] text-cyan-300">
                        {formatBrl(contract?.protected_amount)}
                      </strong>
                    </div>

                    <div className="px-2 py-1.5">
                      <span className="block text-[7px] uppercase text-slate-600">LOCKs ativos</span>
                      <strong className="text-[9px] text-white">{lock.activeCount}</strong>
                    </div>
                  </div>
                )}
              </button>
            );
          })()}

          {/* ===== MOBILE • FOCO NA OPERAÇÃO ===== */}
          <section className={`md:hidden overflow-hidden rounded-2xl border shadow-2xl shadow-black/20 transition-colors duration-300 ${
            accountMode === "demo"
              ? "border-amber-500/35 bg-gradient-to-b from-amber-950/30 to-slate-900"
              : "border-slate-800 bg-slate-900"
          }`}>
            <div className="border-b border-slate-800 p-2.5">
              <div className="grid grid-cols-2 gap-1 rounded-xl border border-slate-800 bg-slate-950/70 p-1">
                <button
                  type="button"
                  onClick={() => switchAccountMode("real")}
                  className={`h-8 rounded-lg text-[10px] font-black transition ${
                    accountMode === "real" ? "bg-emerald-500 text-slate-950" : "text-slate-500"
                  }`}
                >
                  CONTA REAL
                </button>
                <button
                  type="button"
                  onClick={() => switchAccountMode("demo")}
                  className={`h-8 rounded-lg text-[10px] font-black transition ${
                    accountMode === "demo" ? "bg-amber-400 text-slate-950 shadow-[0_0_18px_rgba(245,158,11,.22)]" : "text-slate-500"
                  }`}
                >
                  CONTA DEMO • VIRTUAL
                </button>
              </div>

              <div className="mt-2.5 grid grid-cols-[1.2fr_.9fr_.9fr] gap-1.5">
                <div className="rounded-xl border border-slate-800 bg-slate-950/45 px-2.5 py-2">
                  <p className={`text-[7px] font-bold uppercase tracking-[0.14em] ${accountMode === "demo" ? "text-amber-400" : "text-slate-600"}`}>{accountMode === "demo" ? "Patrimônio virtual" : "Patrimônio"}</p>
                  <p className="mt-0.5 truncate text-[13px] font-black text-white">{formatBrl(portfolioTotalBrl)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-950/45 px-2 py-2">
                  <p className={`text-[7px] font-bold uppercase tracking-[0.14em] ${accountMode === "demo" ? "text-amber-400" : "text-slate-600"}`}>{accountMode === "demo" ? "Virtual disponível" : "Disponível"}</p>
                  <p className="mt-0.5 truncate text-[11px] font-extrabold text-white">{formatBrl(activeBalance)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-950/45 px-2 py-2 text-right">
                  <p className="text-[7px] font-bold uppercase tracking-[0.14em] text-slate-600">Resultado</p>
                  <p className={`mt-0.5 truncate text-[11px] font-extrabold ${totalProfit >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {totalProfit >= 0 ? "+" : ""}{formatBrl(totalProfit)}
                  </p>
                </div>
              </div>

              {hasPosition && (
                <div className="mt-1.5 flex items-center justify-between rounded-lg border border-emerald-500/15 bg-emerald-500/5 px-2.5 py-1.5">
                  <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-300">Posição • {positionSacks.toFixed(2)} sacas</span>
                  <strong className="text-[10px] text-emerald-200">{formatBrl(positionValueBrl)}</strong>
                </div>
              )}
            </div>

            <div className="p-2.5">
              <div className="mb-2 grid grid-cols-2 gap-1 rounded-xl border border-slate-800 bg-slate-950/70 p-1">
                <button type="button" onClick={() => selectInstrument("CAFE_ARABICA")}
                  className={`h-8 rounded-lg text-[9px] font-black transition ${selectedInstrument === "CAFE_ARABICA" ? "bg-cyan-400 text-slate-950" : "text-slate-500"}`}>
                  ARÁBICA
                </button>
                <button type="button" onClick={() => selectInstrument("CAFE_CONILON")}
                  className={`h-8 rounded-lg text-[9px] font-black transition ${selectedInstrument === "CAFE_CONILON" ? "bg-cyan-400 text-slate-950" : "text-slate-500"}`}>
                  CONILON
                </button>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-[13px] font-black text-white">{selectedInstrument === "CAFE_CONILON" ? "Café Conilon • " : "Café Arábica • "}{activeSymbol}</h2>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[7px] font-black uppercase ${
                      quoteIsLive ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-amber-500/25 bg-amber-500/10 text-amber-300"
                    }`}>
                      <span className={`h-1 w-1 rounded-full ${quoteIsLive ? "bg-emerald-400" : "bg-amber-400"}`} />
                      {quoteIsLive ? "AO VIVO" : "AGUARDANDO"}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <strong className="text-[27px] font-black leading-none tracking-tight text-white">{formatQuote(activeQuoteValue, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</strong>
                    <span className="text-[10px] font-bold text-slate-500">{activeQuoteUnit}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-black ${
                    activeChangePct > 0
                      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                      : activeChangePct < 0
                      ? "border-red-500/25 bg-red-500/10 text-red-300"
                      : "border-slate-700 bg-slate-800 text-slate-300"
                  }`}>
                    {activeChangePct > 0 ? "+" : ""}{activeChangePct.toFixed(2)}%
                  </span>
                  <p className="mt-1 text-[7px] text-slate-600">{quoteTimeLabel}</p>
                </div>
              </div>

              <div className="mt-2 grid grid-cols-[1fr_106px] gap-2">
                <MobileCoffeeSparkline candles={chartCandles} />
                <div className="grid grid-rows-2 gap-1.5">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/50 px-2 py-1.5">
                    <p className="text-[7px] uppercase tracking-wider text-slate-600">Saca</p>
                    <strong className="mt-0.5 block truncate text-[10px] text-white">{fairPrice > 0 ? formatBrl(fairPrice) : "--"}</strong>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/50 px-2 py-1.5">
                    <p className="text-[7px] uppercase tracking-wider text-slate-600">Dólar</p>
                    <strong className="mt-0.5 block truncate text-[10px] text-cyan-200">{effectiveUsdBrl > 0 ? `R$ ${formatQuote(effectiveUsdBrl, 4)}` : "--"}</strong>
                  </div>
                </div>
              </div>

              {realAccountNeedsFunding ? (
                <div className="mt-2 overflow-hidden rounded-xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/12 via-slate-950/80 to-cyan-500/10 shadow-[0_10px_30px_rgba(16,185,129,0.10)]">
                  <div className="px-3 py-2.5">
                    <div className="flex items-start gap-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-500/10">
                        <Wallet className="h-4 w-4 text-emerald-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-black uppercase tracking-[0.10em] text-white">Comece a negociar agora</p>
                        <p className="mt-0.5 text-[9px] leading-relaxed text-slate-400">
                          Sua conta real está sem saldo. Deposite qualquer valor via Pix ou teste primeiro na conta demo.
                        </p>
                      </div>
                    </div>

                    <div className="mt-2 grid grid-cols-[1.2fr_.8fr] gap-2">
                      <button
                        type="button"
                        onClick={() => setShowDeposit(true)}
                        className="flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-emerald-300/70 bg-gradient-to-r from-emerald-400 to-cyan-400 px-3 text-[10px] font-black text-slate-950 shadow-[0_8px_24px_rgba(16,185,129,0.18)] transition active:scale-[0.98]"
                      >
                        <DollarSign className="h-4 w-4" />
                        DEPOSITAR VIA PIX
                      </button>
                      <button
                        type="button"
                        onClick={() => switchAccountMode("demo")}
                        className="min-h-[42px] rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 text-[10px] font-black text-amber-200 transition active:scale-[0.98]"
                      >
                        TESTAR DEMO
                      </button>
                    </div>
                    <p className="mt-1.5 text-center text-[8px] font-semibold text-emerald-300/80">Pix rápido • você pode começar com qualquer valor</p>
                  </div>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setTradeSide("sell"); setTradeError(""); setShowTradePanel(true); }}
                    className="min-h-[66px] rounded-xl border border-red-400/70 bg-gradient-to-b from-red-500 via-red-600 to-red-700 px-3 py-2.5 text-left shadow-[0_10px_28px_rgba(239,68,68,0.28)] transition active:scale-[0.97]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-wider text-red-100">BID {formatQuote(activeQuoteData?.bid, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</p>
                        <p className="mt-0.5 text-[17px] font-black tracking-wide text-white">▼ VENDER</p>
                      </div>
                      <TrendingDown className="h-6 w-6 text-white/90" />
                    </div>
                    <p className="mt-1 text-[9px] font-extrabold text-white/90">{sellPrice > 0 ? formatBrl(sellPrice) : "--"} / saca</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTradeSide("buy"); setTradeError(""); setShowTradePanel(true); }}
                    className="min-h-[66px] rounded-xl border border-emerald-300/70 bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-600 px-3 py-2.5 text-left shadow-[0_10px_28px_rgba(16,185,129,0.28)] transition active:scale-[0.97]"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-wider text-emerald-950/80">ASK {formatQuote(activeQuoteData?.ask, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</p>
                        <p className="mt-0.5 text-[17px] font-black tracking-wide text-slate-950">▲ COMPRAR</p>
                      </div>
                      <TrendingUp className="h-6 w-6 text-slate-950/80" />
                    </div>
                    <p className="mt-1 text-[9px] font-extrabold text-slate-950/85">{buyPrice > 0 ? formatBrl(buyPrice) : "--"} / saca</p>
                  </button>
                </div>
              )}

              <div className="mt-2 flex items-center justify-between border-t border-slate-800 pt-2 text-[8px]">
                <div className="flex gap-3 text-slate-500">
                  <span>Máx <b className="text-emerald-300">{formatQuote(activeQuoteData?.high, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</b></span>
                  <span>Mín <b className="text-red-300">{formatQuote(activeQuoteData?.low, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</b></span>
                </div>
                <button
                  type="button"
                  onClick={() => { try { window.location.href = "/graficos.php"; } catch (_) {} }}
                  className="font-black text-cyan-300"
                >
                  GRÁFICO COMPLETO ›
                </button>
              </div>
            </div>
          </section>

          {/* ===== DESKTOP • PAINEL OPERACIONAL V12 ===== */}
          <section className="hidden md:grid md:grid-cols-[minmax(320px,0.78fr)_minmax(0,1.42fr)] xl:grid-cols-[minmax(370px,0.72fr)_minmax(0,1.48fr)] gap-3">
            {/* COLUNA ESQUERDA */}
            <div className="grid content-start gap-3">
              {/* Conta compacta */}
              <div className={`overflow-hidden rounded-2xl border shadow-xl shadow-black/20 transition-colors duration-300 ${
                accountMode === "demo"
                  ? "border-amber-500/35 bg-gradient-to-b from-amber-950/35 to-slate-900"
                  : "border-slate-800 bg-slate-900"
              }`}>
                <div className={`border-b p-3 ${accountMode === "demo" ? "border-amber-500/20" : "border-slate-800"}`}>
                  <div className="grid grid-cols-2 gap-1 rounded-xl border border-slate-800 bg-slate-950/60 p-1">
                    <button
                      type="button"
                      onClick={() => switchAccountMode("real")}
                      className={`h-8 rounded-lg text-[10px] font-black transition ${accountMode === "real" ? "bg-emerald-500 text-slate-950" : "text-slate-400 hover:bg-slate-800"}`}
                    >
                      CONTA REAL
                    </button>
                    <button
                      type="button"
                      onClick={() => switchAccountMode("demo")}
                      className={`h-8 rounded-lg text-[10px] font-black transition ${accountMode === "demo" ? "bg-amber-400 text-slate-950 shadow-[0_0_18px_rgba(245,158,11,.22)]" : "text-slate-400 hover:bg-slate-800"}`}
                    >
                      CONTA DEMO • VIRTUAL
                    </button>
                  </div>

                  <div className="mt-3 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">
                        {accountMode === "demo" ? "DINHEIRO VIRTUAL DISPONÍVEL" : "Saldo disponível"}
                      </p>
                      <p className={`mt-1 text-[24px] font-black leading-none ${accountMode === "demo" ? "text-amber-300" : "text-white"}`}>{formatBrl(activeBalance)}</p>
                      {accountMode === "demo" && (
                        <p className="mt-1.5 text-[8px] font-bold uppercase tracking-wider text-amber-400/80">Saldo para treinamento • sem valor financeiro real</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-[8px] font-bold uppercase tracking-wider text-slate-500">Patrimônio</p>
                      <p className="mt-1 text-sm font-black text-cyan-100">{formatBrl(portfolioTotalBrl)}</p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-1.5">
                    <div className="rounded-lg border border-slate-800 bg-slate-950/45 p-2">
                      <p className="text-[7px] uppercase tracking-wider text-slate-600">Compra</p>
                      <strong className="mt-0.5 block text-[10px] text-white">{buyPrice > 0 ? `${cashSacksAtBuy.toFixed(2)} sacas` : "--"}</strong>
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/45 p-2">
                      <p className="text-[7px] uppercase tracking-wider text-slate-600">Posição</p>
                      <strong className="mt-0.5 block text-[10px] text-emerald-200">{positionSacks.toFixed(2)} sacas</strong>
                    </div>
                    <div className="rounded-lg border border-slate-800 bg-slate-950/45 p-2">
                      <p className="text-[7px] uppercase tracking-wider text-slate-600">Resultado</p>
                      <strong className={`mt-0.5 block truncate text-[10px] ${totalProfit >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        {totalProfit >= 0 ? "+" : ""}{formatBrl(totalProfit)}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Posições abertas no primeiro viewport */}
              <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-xl shadow-black/20">
                <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2.5">
                  <div>
                    <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">Operações em andamento</p>
                    <h3 className="mt-0.5 text-sm font-black text-white">Posições abertas</h3>
                  </div>
                  <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-1 text-[9px] font-black text-emerald-300">
                    {openOrders.length} aberta{openOrders.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="max-h-[245px] overflow-auto">
                  {openOrders.length === 0 ? (
                    <div className="flex h-[112px] items-center justify-center px-4 text-center">
                      <div>
                        <p className="text-xs font-bold text-slate-400">Nenhuma posição aberta</p>
                        <p className="mt-1 text-[9px] text-slate-600">As novas operações aparecerão aqui automaticamente.</p>
                      </div>
                    </div>
                  ) : (
                    <table className="w-full table-fixed text-left">
                      <thead className="sticky top-0 z-10 bg-slate-950/95">
                        <tr className="text-[7px] font-black uppercase tracking-wider text-slate-600">
                          <th className="w-[16%] px-2 py-2">Ordem</th>
                          <th className="w-[18%] px-2 py-2">Entrada</th>
                          <th className="w-[18%] px-2 py-2">Atual</th>
                          <th className="w-[24%] px-2 py-2 text-right">Resultado</th>
                          <th className="w-[24%] px-2 py-2 text-right">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openOrders.slice(0, 8).map((o) => {
                          const side = String(o?.side || "").toLowerCase();
                          const instrument = String(o?.instrument || "CAFE_ARABICA").toUpperCase();
                          const orderFairPrice = instrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
                          const entry = toNumber(o?.entry_price_saca ?? o?.saca_entry_price ?? o?.entry_price ?? o?.price, 0);
                          const qty = Math.max(0, toNumber(o?.saca_qty ?? o?.qty_saca ?? o?.cofy_qty ?? o?.qty, 0));
                          const apiPnl = Number(o?.profit_loss ?? o?.profit_loss_brl ?? o?.profit ?? o?.pnl);
                          const calcPnl = entry > 0 && orderFairPrice > 0 && qty > 0
                            ? (side === "sell" ? (entry - orderFairPrice) : (orderFairPrice - entry)) * qty
                            : 0;
                          const pnl = Number.isFinite(apiPnl) ? apiPnl : calcPnl;
                          return (
                            <tr key={o?.id} className="border-t border-slate-800/80 text-[9px]">
                              <td className="px-2 py-2">
                                <strong className="block truncate text-white">#{o?.id}</strong>
                                <span className={`text-[7px] font-black ${side === "sell" ? "text-red-300" : "text-emerald-300"}`}>
                                  {side === "sell" ? "VENDA" : "COMPRA"}
                                </span>
                              </td>
                              <td className="truncate px-2 py-2 font-bold text-slate-300">{entry > 0 ? formatBrl(entry) : "--"}</td>
                              <td className="truncate px-2 py-2 font-bold text-cyan-200">{orderFairPrice > 0 ? formatBrl(orderFairPrice) : "--"}</td>
                              <td className={`truncate px-2 py-2 text-right font-black ${pnl >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                {pnl >= 0 ? "+" : ""}{formatBrl(pnl)}
                              </td>
                              <td className="px-2 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => openOpenOrderManager(o)}
                                  className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-2 py-1.5 text-[7px] font-black text-cyan-200 transition hover:bg-cyan-400/20"
                                >
                                  GERENCIAR
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {openOrders.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowHistoryPanel(true)}
                    className="w-full border-t border-slate-800 px-3 py-2 text-[9px] font-black text-cyan-300 transition hover:bg-slate-800/50"
                  >
                    VER TODAS AS POSIÇÕES / HISTÓRICO ›
                  </button>
                )}
              </div>

              {/* Ordens pendentes visíveis no desktop */}
              {pendingOrders.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-amber-500/25 bg-slate-900 shadow-xl shadow-black/20">
                  <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2.5">
                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-amber-400/80">Aguardando preço</p>
                      <h3 className="mt-0.5 text-sm font-black text-white">Ordens pendentes</h3>
                    </div>
                    <span className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2 py-1 text-[9px] font-black text-amber-300">
                      {pendingOrders.length} pendente{pendingOrders.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="divide-y divide-slate-800">
                    {pendingOrders.slice(0, 3).map((o) => (
                      <div key={`desktop-pending-${o.id}`} className="px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-[10px] font-black text-white">
                              Ordem #{o.id} • {String(o?.instrument || "CAFE_ARABICA").toUpperCase() === "CAFE_CONILON" ? "CONILON" : "ARÁBICA"}
                            </p>
                            <p className={`mt-0.5 text-[8px] font-bold ${String(o?.side || "").toLowerCase() === "sell" ? "text-red-300" : "text-emerald-300"}`}>
                              {String(o?.side || "").toLowerCase() === "sell" ? "VENDA" : "COMPRA"} {String(o?.order_type || "limit").toUpperCase()} • gatilho {formatBrl(o?.trigger_price)}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openPendingOrderEditor(o)}
                              className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1.5 text-[7px] font-black text-cyan-200 hover:bg-cyan-400/20"
                            >
                              GERENCIAR
                            </button>
                            <button
                              type="button"
                              onClick={() => cancelPendingOrder(o.id)}
                              disabled={cancelingPendingId === o.id}
                              className="rounded-lg border border-red-500/25 bg-red-500/10 px-2.5 py-1.5 text-[7px] font-black text-red-200 hover:bg-red-500/20 disabled:opacity-50"
                            >
                              {cancelingPendingId === o.id ? "..." : "CANCELAR"}
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-[8px] text-slate-500">
                          <span>Reservado: <b className="text-slate-300">{formatBrl(o?.amount)}</b></span>
                          <span>SL: <b className="text-red-300">{o?.stop_loss ? formatBrl(o.stop_loss) : "—"}</b></span>
                          <span>TP: <b className="text-emerald-300">{o?.take_profit ? formatBrl(o.take_profit) : "—"}</b></span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {pendingOrders.length > 3 && (
                    <button
                      type="button"
                      onClick={() => { setOperationsTab("pending"); setShowHistoryPanel(true); }}
                      className="w-full border-t border-slate-800 px-3 py-2 text-[9px] font-black text-amber-300 hover:bg-slate-800/50"
                    >
                      VER TODAS AS ORDENS PENDENTES ›
                    </button>
                  )}
                </div>
              )}

              {/* Parceria integrada à coluna */}
              {partnerData?.is_partner === true && (
                <button
                  type="button"
                  onClick={openPartnerPanel}
                  className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-slate-900 to-emerald-950/25 p-3 text-left shadow-xl shadow-black/20 transition hover:border-emerald-400/40"
                >
                  <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-emerald-400/10 blur-2xl" />
                  <div className="relative flex items-center gap-3">
                    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/12 text-emerald-300">
                      <DollarSign className="h-5 w-5" />
                      {newPartnerCommissions > 0 && (
                        <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-black text-white">
                          {newPartnerCommissions > 99 ? "99+" : newPartnerCommissions}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-500">Programa de parceiros</p>
                      <div className="mt-0.5 flex items-baseline justify-between gap-2">
                        <h3 className="text-sm font-black text-white">Minha Parceria</h3>
                        <strong className="text-sm text-emerald-300">{formatBrl(partnerAvailable)}</strong>
                      </div>
                      <p className="mt-1 text-[9px] text-slate-500">{partnerReferralCount} indicado{partnerReferralCount === 1 ? "" : "s"} • clique para abrir o painel</p>
                    </div>
                    <span className="text-xl text-slate-500">›</span>
                  </div>
                </button>
              )}
            </div>

            {/* COLUNA DIREITA • MERCADO + GRÁFICO + AÇÕES */}
            <div className={`min-w-0 overflow-hidden rounded-2xl border shadow-2xl shadow-black/20 transition-colors duration-300 ${
              accountMode === "demo"
                ? "border-amber-500/25 bg-slate-900 shadow-amber-950/10"
                : "border-slate-800 bg-slate-900"
            }`}>
              <div className="flex items-center justify-between gap-4 border-b border-slate-800 px-4 py-3">
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex rounded-xl border border-slate-800 bg-slate-950/70 p-1">
                    <button type="button" onClick={() => selectInstrument("CAFE_ARABICA")}
                      className={`rounded-lg px-3 py-1.5 text-[8px] font-black transition ${selectedInstrument === "CAFE_ARABICA" ? "bg-cyan-400 text-slate-950" : "text-slate-500"}`}>
                      ARÁBICA
                    </button>
                    <button type="button" onClick={() => selectInstrument("CAFE_CONILON")}
                      className={`rounded-lg px-3 py-1.5 text-[8px] font-black transition ${selectedInstrument === "CAFE_CONILON" ? "bg-cyan-400 text-slate-950" : "text-slate-500"}`}>
                      CONILON
                    </button>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">Mercado agora</p>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[7px] font-black uppercase ${quoteIsLive ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-amber-500/25 bg-amber-500/10 text-amber-300"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${quoteIsLive ? "animate-pulse bg-emerald-400" : "bg-amber-400"}`} />
                        {quoteIsLive ? "ATIVO" : "AGUARDANDO"}
                      </span>
                    </div>
                    <h2 className="mt-0.5 text-sm font-black text-white">{selectedInstrument === "CAFE_CONILON" ? "Café Conilon • " : "Café Arábica • "}{activeSymbol}</h2>
                  </div>
                  <div className="h-8 w-px bg-slate-800" />
                  <div>
                    <div className="flex items-baseline gap-1.5">
                      <strong className="text-2xl font-black tracking-tight text-white">{formatQuote(activeQuoteValue, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</strong>
                      <span className="text-[9px] font-bold text-slate-500">{activeQuoteUnit}</span>
                    </div>
                    <p className="text-[8px] text-slate-600">{quoteTimeLabel}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/45 px-3 py-2 text-right">
                    <p className="text-[7px] uppercase tracking-wider text-slate-600">Saca referência</p>
                    <strong className="text-[11px] text-white">{fairPrice > 0 ? formatBrl(fairPrice) : "--"}</strong>
                  </div>
                  <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${activeChangePct > 0 ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : activeChangePct < 0 ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-slate-700 bg-slate-800 text-slate-300"}`}>
                    {activeChangePct > 0 ? "+" : ""}{kcChangePct.toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="p-3">
                <ProfessionalCoffeeChart candles={chartCandles} symbol={activeSymbol} error={chartError} />

                <div className="mt-3 grid grid-cols-[.72fr_1fr_1fr] gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-2.5">
                      <p className="text-[7px] font-bold uppercase tracking-wider text-slate-600">Dólar</p>
                      <strong className="mt-1 block text-[11px] text-cyan-200">{effectiveUsdBrl > 0 ? `R$ ${formatQuote(effectiveUsdBrl, 4)}` : "--"}</strong>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-2.5">
                      <p className="text-[7px] font-bold uppercase tracking-wider text-slate-600">Máx / Mín</p>
                      <strong className="mt-1 block text-[10px] text-white">{formatQuote(activeQuoteData?.high, selectedInstrument === "CAFE_CONILON" ? 0 : 2)} / {formatQuote(activeQuoteData?.low, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</strong>
                    </div>
                  </div>

                  {realAccountNeedsFunding ? (
                    <div className="col-span-2 flex min-h-[82px] items-center justify-between gap-4 rounded-2xl border border-emerald-400/30 bg-gradient-to-r from-emerald-500/10 via-slate-950/80 to-cyan-500/10 px-4 py-3 shadow-[0_12px_34px_rgba(16,185,129,0.10)]">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-500/10">
                          <Wallet className="h-5 w-5 text-emerald-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-black uppercase tracking-[0.10em] text-white">Comece a negociar agora</p>
                          <p className="mt-1 text-[10px] text-slate-400">Deposite qualquer valor via Pix ou conheça primeiro a conta demo.</p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => switchAccountMode("demo")}
                          className="h-11 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 text-[10px] font-black text-amber-200 transition hover:bg-amber-400/15 active:scale-[0.98]"
                        >
                          TESTAR DEMO
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeposit(true)}
                          className="flex h-11 items-center gap-2 rounded-xl border border-emerald-300/70 bg-gradient-to-r from-emerald-400 to-cyan-400 px-5 text-[10px] font-black text-slate-950 shadow-[0_8px_24px_rgba(16,185,129,0.18)] transition hover:-translate-y-0.5 active:scale-[0.98]"
                        >
                          <DollarSign className="h-4 w-4" />
                          DEPOSITAR VIA PIX
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { setTradeSide("sell"); setTradeError(""); setShowTradePanel(true); }}
                        className="group min-h-[82px] rounded-2xl border border-red-400/70 bg-gradient-to-b from-red-500 via-red-600 to-red-700 px-4 py-3 text-left shadow-[0_12px_34px_rgba(239,68,68,0.24)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_38px_rgba(239,68,68,0.30)] active:scale-[0.98]"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black uppercase tracking-wider text-red-100">BID {formatQuote(activeQuoteData?.bid, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</p>
                            <p className="mt-1 text-xl font-black tracking-wide text-white">▼ VENDER</p>
                          </div>
                          <TrendingDown className="h-7 w-7 text-white/90" />
                        </div>
                        <p className="mt-1 text-[11px] font-extrabold text-white/90">{sellPrice > 0 ? formatBrl(sellPrice) : "--"} / saca</p>
                      </button>

                      <button
                        type="button"
                        onClick={() => { setTradeSide("buy"); setTradeError(""); setShowTradePanel(true); }}
                        className="group min-h-[82px] rounded-2xl border border-emerald-300/70 bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-600 px-4 py-3 text-left shadow-[0_12px_34px_rgba(16,185,129,0.24)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_38px_rgba(16,185,129,0.30)] active:scale-[0.98]"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black uppercase tracking-wider text-emerald-950/80">ASK {formatQuote(activeQuoteData?.ask, selectedInstrument === "CAFE_CONILON" ? 0 : 2)}</p>
                            <p className="mt-1 text-xl font-black tracking-wide text-slate-950">▲ COMPRAR</p>
                          </div>
                          <TrendingUp className="h-7 w-7 text-slate-950/80" />
                        </div>
                        <p className="mt-1 text-[11px] font-extrabold text-slate-950/85">{buyPrice > 0 ? formatBrl(buyPrice) : "--"} / saca</p>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ===== Minha Parceria - botão compacto mobile ===== */}
          {partnerData?.is_partner === true && (
            <button
              type="button"
              onClick={openPartnerPanel}
              className="relative w-full md:hidden rounded-2xl border border-emerald-500/25 bg-slate-900 p-3 text-left shadow-lg transition active:scale-[0.99]"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">
                  <DollarSign className="h-5 w-5" />
                  {newPartnerCommissions > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black text-white ring-2 ring-slate-900">
                      {newPartnerCommissions > 99 ? "99+" : newPartnerCommissions}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-black text-white">Minha Parceria</p>
                    {newPartnerCommissions > 0 && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-red-300">
                        <BellRing className="h-3 w-3" />
                        Nova comissão
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">
                    <strong className="text-emerald-300">{formatBrl(partnerAvailable)}</strong> disponível
                    <span className="mx-1.5 text-slate-700">•</span>
                    {partnerReferralCount} indicado{partnerReferralCount === 1 ? "" : "s"}
                  </p>
                </div>

                <span className="shrink-0 text-xl text-slate-500">›</span>
              </div>
            </button>
          )}

          {/* ===== Operações compactas MOBILE ===== */}
          <div className="md:hidden grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setOperationsTab("open"); setShowHistoryPanel(true); }}
              className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900 p-3 text-left active:scale-[.99]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] font-bold uppercase tracking-[.12em] text-slate-500">Em aberto</span>
                <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-black text-white">{openOrders.length}</span>
              </div>
              <p className={`mt-2 truncate text-base font-black ${openProfitLoss >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                {openProfitLoss >= 0 ? "+" : ""}{formatBrl(openProfitLoss)}
              </p>
              <p className="mt-1 truncate text-[9px] text-slate-500">{formatBrl(openInvestedTotal)} em posições • ver ›</p>
            </button>

            <button
              type="button"
              onClick={() => { setOperationsTab("pending"); setShowHistoryPanel(true); }}
              className="min-w-0 rounded-2xl border border-amber-500/20 bg-slate-900 p-3 text-left active:scale-[.99]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[9px] font-bold uppercase tracking-[.12em] text-amber-400/80">Pendentes</span>
                <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-black text-amber-300">{pendingOrders.length}</span>
              </div>
              <p className="mt-2 truncate text-base font-black text-white">{formatBrl(pendingReservedTotal)}</p>
              <p className="mt-1 truncate text-[9px] text-slate-500">valor reservado • ver ›</p>
            </button>
          </div>

          {showHistoryPanel && (
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2 sm:pb-3">
                <CardTitle className="text-base sm:text-lg text-white">Operações</CardTitle>
              </CardHeader>
              <CardContent className="pt-1 sm:pt-2">
                <Tabs value={operationsTab} onValueChange={setOperationsTab}>
                  <TabsList className="w-full bg-slate-800 mb-3 flex">
                    <TabsTrigger value="open" className="flex-1 text-xs sm:text-sm text-white">Abertas ({openOrders.length})</TabsTrigger>
                    <TabsTrigger value="pending" className="flex-1 text-xs sm:text-sm text-white">Pendentes ({pendingOrders.length})</TabsTrigger>
                    <TabsTrigger value="closed" className="flex-1 text-xs sm:text-sm text-white">Histórico ({closedOrders.length})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="open">
                    <div className="mb-2 md:hidden rounded-xl border border-slate-800 bg-slate-950/40 px-3 py-2 text-[10px] text-slate-400">
                      Toque em <strong className="text-white">Ver ordem</strong> / ação da posição para abrir os detalhes, encerrar ou editar Stop Loss e Take Profit.
                    </div>
                    <div className="overflow-x-auto -mx-2 sm:mx-0">
                      <OrdersTable
                        orders={openOrders}
                        marketData={marketDataForOrders}
                        marketPrices={{ CAFE_ARABICA: arabicaFairPrice, CAFE_CONILON: conilonFairPrice }}
                        onOrderClosed={handleOrderClosed}
                        userSettings={userSettingsNoMinClose}
                        accountMode={accountMode}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="pending">
                      <div className="space-y-3">
                        {pendingOrders.length === 0 ? (
                          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-400">Nenhuma ordem pendente.</div>
                        ) : pendingOrders.map((o) => {
                          const pendingInstrument = String(o?.instrument || "CAFE_ARABICA").toUpperCase();
                          const pendingFairPrice = pendingInstrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
                          return (
                          <div key={o.id} className="rounded-xl border border-amber-500/20 bg-slate-900 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-black text-white">Ordem #{o.id}</p>
                                <p className={`mt-1 text-xs font-bold ${o.side === "buy" ? "text-emerald-300" : "text-red-300"}`}>{o.side === "buy" ? "COMPRA" : "VENDA"} {String(o.order_type || "").toUpperCase()} • PENDENTE</p>
                              </div>
                              <span className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-black text-amber-300">AGUARDANDO PREÇO</span>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                              <div className="rounded-lg bg-slate-800/60 p-2"><p className="text-slate-400">Valor reservado</p><strong className="text-white">{formatBrl(o.amount)}</strong></div>
                              <div className="rounded-lg bg-slate-800/60 p-2"><p className="text-slate-400">{o.order_type === "stop" ? "Preço de disparo" : "Preço limite"}</p><strong className="text-cyan-300">{formatBrl(o.trigger_price)}</strong></div>
                            </div>
                            <p className="mt-3 text-[11px] text-slate-400">Preço atual da saca: <strong className="text-white">{formatBrl(pendingFairPrice)}</strong></p>
                            {(o?.stop_loss || o?.take_profit) && (
                              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                                <div className="rounded-lg border border-red-500/15 bg-red-500/5 px-2.5 py-2">
                                  <span className="block text-slate-500">Stop Loss</span>
                                  <strong className="text-red-300">{o?.stop_loss ? formatBrl(o.stop_loss) : "—"}</strong>
                                </div>
                                <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/5 px-2.5 py-2">
                                  <span className="block text-slate-500">Take Profit</span>
                                  <strong className="text-emerald-300">{o?.take_profit ? formatBrl(o.take_profit) : "—"}</strong>
                                </div>
                              </div>
                            )}
                            <div className="mt-3 grid grid-cols-2 gap-2">
                              <Button
                                type="button"
                                onClick={() => openPendingOrderEditor(o)}
                                className="h-11 rounded-xl border border-cyan-400/30 bg-cyan-400/10 text-xs font-black text-cyan-200 hover:bg-cyan-400/20"
                              >
                                GERENCIAR
                              </Button>
                              <Button
                                type="button"
                                onClick={() => cancelPendingOrder(o.id)}
                                disabled={cancelingPendingId === o.id}
                                className="h-11 rounded-xl border border-red-500/25 bg-red-500/10 text-xs font-black text-red-200 hover:bg-red-500/20 disabled:opacity-60"
                              >
                                {cancelingPendingId === o.id ? "CANCELANDO..." : "CANCELAR"}
                              </Button>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </TabsContent>
                  <TabsContent value="closed">
                    <div className="overflow-x-auto -mx-2 sm:mx-0">
                      <OrdersTable
                        orders={closedOrders}
                        userSettings={userSettingsNoMinClose}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>
      </div>



      {/* ===== Painel de nova operação ===== */}
      {showTradePanel && !realAccountNeedsFunding && (
        <div className="fixed inset-0 z-[145] flex items-end justify-center bg-black/85 backdrop-blur-md sm:items-center sm:p-4" onClick={() => setShowTradePanel(false)}>
          <div className="w-full max-h-[94vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
          <Card className={`w-full max-w-lg overflow-hidden border-2 bg-slate-900 shadow-2xl ${tradeSide === "buy" ? "border-emerald-500/45 shadow-emerald-950/40" : "border-red-500/45 shadow-red-950/40"}`}>
            <CardHeader className={`border-b pb-3 ${tradeSide === "buy" ? "border-emerald-500/20 bg-gradient-to-r from-emerald-500/15 via-slate-900 to-slate-900" : "border-red-500/20 bg-gradient-to-r from-red-500/15 via-slate-900 to-slate-900"}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {accountMode === "demo" ? "CONTA DEMO • DINHEIRO VIRTUAL" : "CONTA REAL • NOVA ORDEM"}
                  </p>
                  <CardTitle className="mt-1 text-base text-white">
                    {accountMode === "demo" ? "Simular operação" : "Abrir operação"}
                  </CardTitle>
                </div>
                <button type="button" aria-label="Fechar ordem" title="Fechar" onClick={() => setShowTradePanel(false)} className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-white/20 bg-slate-950 text-white shadow-lg transition hover:scale-105 hover:border-white/40 hover:bg-slate-800 active:scale-95"><X className="h-6 w-6" /></button>
                <span className={`hidden sm:inline-flex rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${tradeSide === "buy" ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300" : "border-red-500/20 bg-red-500/10 text-red-300"}`}>
                  {tradeSide === "buy" ? "Alta selecionada" : "Baixa selecionada"}
                </span>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-4">
              {accountMode === "demo" && (
                <div className="rounded-2xl border border-cyan-400/25 bg-cyan-400/10 px-3 py-2.5">
                  <p className="text-[10px] font-black uppercase tracking-[0.16em] text-cyan-300">Conta Demo ativa</p>
                  <p className="mt-1 text-xs text-slate-300">Saldo virtual: {formatBrl(activeBalance)}. Nenhuma operação desta tela será enviada para a conta real.</p>
                </div>
              )}
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Tipo de ordem</p>
                  {orderType === "limit" && <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-cyan-300">{accountMode === "demo" ? "ATIVO NO DEMO" : "ATIVO NO REAL"}</span>}
                  {orderType === "stop" && <span className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2 py-1 text-[9px] font-black uppercase tracking-wider text-cyan-300">{accountMode === "demo" ? "ATIVO NO DEMO" : "ATIVO NO REAL"}</span>}
                </div>
                <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-slate-950/60 p-1.5">
                  {[
                    { id: "market", label: "Mercado", sub: "Agora" },
                    { id: "limit", label: "Limite", sub: "Seu preço" },
                    { id: "stop", label: "Stop", sub: "Disparo" },
                  ].map((item) => (
                    <button key={item.id} type="button" onClick={() => { setOrderType(item.id); setTradeError(""); }} className={`rounded-xl border px-2 py-2.5 text-center transition ${orderType === item.id ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-transparent text-slate-500 hover:bg-slate-800/70"}`}>
                      <span className="block text-[11px] font-black">{item.label}</span>
                      <span className="mt-0.5 block text-[8px]">{item.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-950/50 p-1.5">
                <button
                  type="button"
                  onClick={() => { setTradeSide("buy"); setTradeError(""); }}
                  className={[
                    "flex min-h-[58px] items-center gap-2 rounded-xl px-3 text-left transition-all duration-200",
                    tradeSide === "buy"
                      ? "bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20"
                      : "text-slate-400 hover:bg-slate-800/80",
                  ].join(" ")}
                >
                  <TrendingUp className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-xs font-black">COMPRAR</p>
                    <p className={`text-[9px] ${tradeSide === "buy" ? "text-slate-900/70" : "text-slate-500"}`}>Lucrar com alta</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => { setTradeSide("sell"); setTradeError(""); }}
                  className={[
                    "flex min-h-[58px] items-center gap-2 rounded-xl px-3 text-left transition-all duration-200",
                    tradeSide === "sell"
                      ? "bg-red-500 text-white shadow-lg shadow-red-500/20"
                      : "text-slate-400 hover:bg-slate-800/80",
                  ].join(" ")}
                >
                  <TrendingDown className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-xs font-black">VENDER</p>
                    <p className={`text-[9px] ${tradeSide === "sell" ? "text-white/70" : "text-slate-500"}`}>Lucrar com baixa</p>
                  </div>
                </button>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-slate-300">Valor da operação</label>
                  <span className="text-[10px] text-slate-500">Saldo: {formatBrl(activeBalance)}</span>
                </div>

                <div className={`rounded-2xl border bg-slate-950 px-4 py-3 transition ${tradeSide === "buy" ? "border-emerald-500/30 focus-within:border-emerald-400/70" : "border-red-500/30 focus-within:border-red-400/70"}`}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={tradeAmountDisplay}
                    onChange={(e) => handleTradeAmountMasked(e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-full bg-transparent text-2xl font-black tracking-tight text-white outline-none placeholder:text-slate-700"
                  />
                  <p className="mt-1 text-[10px] text-slate-500">Digite o valor que deseja movimentar.</p>
                </div>

                <div className="mt-2 grid grid-cols-5 gap-1.5">
                  {[100, 500, 1000, 5000].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setQuickTradeAmount(value)}
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-1 py-2 text-[9px] font-bold text-slate-300 transition hover:border-slate-600 active:scale-95"
                    >
                      {value >= 1000 ? `${value / 1000} mil` : value}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setQuickTradeAmount(activeBalance)}
                    className="rounded-lg border border-slate-700 bg-slate-800 px-1 py-2 text-[9px] font-black text-white transition hover:bg-slate-700 active:scale-95"
                  >
                    MÁX.
                  </button>
                </div>
              </div>

              {orderType !== "market" && (
                <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-white">{orderType === "limit" ? "Preço limite" : "Preço de disparo"}</p>
                      <p className="mt-0.5 text-[9px] text-slate-500">Arraste pela porcentagem ou digite o preço. Os dois ficam sincronizados.</p>
                    </div>
                    <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-1 text-[8px] font-bold text-amber-300">±10%</span>
                  </div>
                  <PricePercentSlider
                    label={orderType === "limit" ? "Limite da ordem" : "Disparo da ordem"}
                    hint={tradeSide === "buy"
                      ? (orderType === "limit" ? "Compra Limite: normalmente abaixo do preço atual." : "Compra Stop: normalmente acima do preço atual.")
                      : (orderType === "limit" ? "Venda Limite: normalmente acima do preço atual." : "Venda Stop: normalmente abaixo do preço atual.")}
                    value={triggerPrice}
                    onChange={setTriggerPrice}
                    referencePrice={fairPrice}
                    accent="cyan"
                  />
                </div>
              )}

              <div className="rounded-2xl border border-slate-800 bg-slate-950/35 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black text-white">Proteção da operação</p>
                    <p className="mt-0.5 text-[9px] text-slate-500">Defina por preço ou porcentagem. A referência é o preço atual da saca.</p>
                  </div>
                  <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[8px] font-bold uppercase text-emerald-300">ATIVO</span>
                </div>
                <div className="space-y-2">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-2.5">
                    <div className="mb-2 flex items-center gap-2">
                      <button type="button" onClick={() => setStopLossEnabled(!stopLossEnabled)} className={`relative h-6 w-10 shrink-0 rounded-full transition ${stopLossEnabled ? "bg-red-500" : "bg-slate-700"}`}><span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${stopLossEnabled ? "left-5" : "left-1"}`} /></button>
                      <div className="min-w-0 flex-1"><p className="text-[11px] font-bold text-slate-200">Stop Loss</p><p className="text-[8px] text-slate-600">Limitar perda automaticamente</p></div>
                    </div>
                    <PricePercentSlider
                      label="Preço do Stop Loss"
                      hint={tradeSide === "buy" ? "Na compra, mova para a esquerda (percentual negativo)." : "Na venda, mova para a direita (percentual positivo)."}
                      value={stopLossPrice}
                      onChange={setStopLossPrice}
                      referencePrice={fairPrice}
                      disabled={!stopLossEnabled}
                      accent="red"
                    />
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-2.5">
                    <div className="mb-2 flex items-center gap-2">
                      <button type="button" onClick={() => setTakeProfitEnabled(!takeProfitEnabled)} className={`relative h-6 w-10 shrink-0 rounded-full transition ${takeProfitEnabled ? "bg-emerald-500" : "bg-slate-700"}`}><span className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${takeProfitEnabled ? "left-5" : "left-1"}`} /></button>
                      <div className="min-w-0 flex-1"><p className="text-[11px] font-bold text-slate-200">Take Profit</p><p className="text-[8px] text-slate-600">Realizar lucro automaticamente</p></div>
                    </div>
                    <PricePercentSlider
                      label="Preço do Take Profit"
                      hint={tradeSide === "buy" ? "Na compra, mova para a direita (percentual positivo)." : "Na venda, mova para a esquerda (percentual negativo)."}
                      value={takeProfitPrice}
                      onChange={setTakeProfitPrice}
                      referencePrice={fairPrice}
                      disabled={!takeProfitEnabled}
                      accent="emerald"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Preço de entrada</p>
                  <p className={`mt-1 text-sm font-extrabold ${tradeSide === "buy" ? "text-emerald-200" : "text-red-200"}`}>
                    {selectedEntryPrice > 0 ? formatBrl(selectedEntryPrice) : "--"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Estimativa</p>
                  <p className="mt-1 text-sm font-extrabold text-white">
                    {selectedEstimatedQty > 0 ? `${selectedEstimatedQty.toFixed(4)} saca(s)` : "--"}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/30 px-3 py-2.5">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Spread da plataforma</p>
                  <p className="mt-0.5 text-xs font-bold text-slate-200">{spreadPct.toFixed(2)}%</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Direção</p>
                  <div className={`mt-0.5 flex items-center justify-end gap-1 text-xs font-bold ${tradeSide === "buy" ? "text-emerald-300" : "text-red-300"}`}>
                    {tradeSide === "buy" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {tradeSide === "buy" ? "Alta" : "Baixa"}
                  </div>
                </div>
              </div>

              {fairPrice <= 0 && (
                <p className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
                  Aguardando cotação da bolsa + dólar para calcular o preço da saca.
                </p>
              )}

              {tradeError && (
                <p className="rounded-xl border border-red-500/20 bg-red-500/5 px-3 py-2 text-[11px] text-red-300">
                  {tradeError}
                </p>
              )}

              {tradeSide === "buy" ? (
                <Button
                  type="button"
                  disabled={buyLoading || fairPrice <= 0 || tradeAmountNum <= 0}
                  onClick={() => requestTradeConfirmation("buy")}
                  className="h-14 w-full rounded-2xl bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400 text-slate-950 shadow-xl shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:shadow-emerald-500/30 active:translate-y-0 disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    <span className="font-black tracking-wide">
                      {buyLoading ? "ENVIANDO..." : orderType === "limit" ? `CRIAR COMPRA LIMITE • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : ""}` : orderType === "stop" ? `CRIAR COMPRA STOP • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : ""}` : `COMPRAR • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "ALTA"}`}
                    </span>
                  </span>
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={sellLoading || fairPrice <= 0 || tradeAmountNum <= 0}
                  onClick={() => requestTradeConfirmation("sell")}
                  className="h-14 w-full rounded-2xl bg-gradient-to-r from-red-400 via-red-500 to-red-400 text-white shadow-xl shadow-red-500/20 transition hover:-translate-y-0.5 hover:shadow-red-500/30 active:translate-y-0 disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <TrendingDown className="h-5 w-5" />
                    <span className="font-black tracking-wide">
                      {sellLoading ? "ENVIANDO..." : orderType === "limit" ? `CRIAR VENDA LIMITE • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : ""}` : orderType === "stop" ? `CRIAR VENDA STOP • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : ""}` : `VENDER • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "BAIXA"}`}
                    </span>
                  </span>
                </Button>
              )}

              <p className="text-center text-[9px] leading-relaxed text-slate-600">
                Confira direção, valor e preço de entrada antes de confirmar a operação.
              </p>
            </CardContent>
          </Card>

          </div>
        </div>
      )}

      {/* ===== Gerenciar posição ABERTA • visível no desktop e mobile ===== */}
      {openManageOrder && (() => {
        const manageInstrument = String(openManageOrder?.instrument || "CAFE_ARABICA").toUpperCase();
        const manageFair = manageInstrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
        const manageSide = String(openManageOrder?.side || "buy").toLowerCase();

        return (
          <div className="fixed inset-0 z-[188] flex items-end justify-center bg-black/85 backdrop-blur-md sm:items-center sm:p-4" onClick={closeOpenOrderManager}>
            <div className="max-h-[94vh] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-800 bg-slate-950/95 px-4 py-4 backdrop-blur">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.18em] text-cyan-300">POSIÇÃO ABERTA #{openManageOrder.id}</p>
                  <h3 className="mt-1 text-xl font-black text-white">Gerenciar Stop Loss / Target</h3>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {manageInstrument === "CAFE_CONILON" ? "Café Conilon" : "Café Arábica"} • {manageSide === "buy" ? "COMPRA" : "VENDA"} • preço atual {manageFair > 0 ? formatBrl(manageFair) : "—"}
                  </p>
                </div>
                <button type="button" onClick={closeOpenOrderManager} disabled={openManageSaving} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-white"><X className="h-5 w-5" /></button>
              </div>

              <div className="space-y-4 p-4 sm:p-5">
                <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-xs font-black text-white">Proteção da posição</p>
                  <p className="mt-1 text-[10px] leading-relaxed text-slate-400">
                    Você pode adicionar, aumentar, reduzir ou remover o Stop Loss e o Take Profit a qualquer momento enquanto a posição estiver aberta.
                  </p>
                </div>

                <PricePercentSlider
                  label="Stop Loss"
                  hint={manageSide === "buy" ? "Na compra, use um preço abaixo do mercado atual." : "Na venda, use um preço acima do mercado atual."}
                  value={openManageStopLoss}
                  onChange={setOpenManageStopLoss}
                  referencePrice={manageFair}
                  accent="red"
                />

                <PricePercentSlider
                  label="Take Profit / Target"
                  hint={manageSide === "buy" ? "Na compra, use um preço acima do mercado atual." : "Na venda, use um preço abaixo do mercado atual."}
                  value={openManageTakeProfit}
                  onChange={setOpenManageTakeProfit}
                  referencePrice={manageFair}
                  accent="emerald"
                />

                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setOpenManageStopLoss(""); setOpenManageTakeProfit(""); setOpenManageError(""); }}
                    disabled={openManageSaving}
                    className="h-11 rounded-xl border border-slate-700 bg-slate-900 text-[10px] font-black text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                  >
                    REMOVER SL E TP
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOpenManageStopLoss(""); setOpenManageError(""); }}
                    disabled={openManageSaving}
                    className="h-11 rounded-xl border border-red-500/20 bg-red-500/5 text-[10px] font-black text-red-200 hover:bg-red-500/10 disabled:opacity-50"
                  >
                    REMOVER SÓ STOP
                  </button>
                </div>

                {openManageError && (
                  <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-xs text-red-300">{openManageError}</div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={closeOpenOrderManager} disabled={openManageSaving} className="h-12 rounded-2xl bg-slate-800 text-xs font-black text-white hover:bg-slate-700">VOLTAR</Button>
                  <Button type="button" onClick={saveOpenOrderProtection} disabled={openManageSaving} className="h-12 rounded-2xl bg-cyan-400 text-xs font-black text-slate-950 hover:bg-cyan-300 disabled:opacity-50">{openManageSaving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}</Button>
                </div>

                <p className="text-center text-[9px] text-slate-600">
                  Para remover somente o Take Profit, deixe o campo Take Profit vazio e salve.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== Gerenciar ordem pendente LIMIT / STOP ===== */}
      {pendingEditOrder && (() => {
        const editInstrument = String(pendingEditOrder?.instrument || "CAFE_ARABICA").toUpperCase();
        const editFair = editInstrument === "CAFE_CONILON" ? conilonFairPrice : arabicaFairPrice;
        const editSide = String(pendingEditOrder?.side || "buy").toLowerCase();
        const editTriggerNum = Number(String(pendingEditTrigger || "").replace(",", ".")) || 0;
        const protectionRef = editTriggerNum > 0 ? editTriggerNum : editFair;

        return (
          <div className="fixed inset-0 z-[185] flex items-end justify-center bg-black/85 backdrop-blur-md sm:items-center sm:p-4" onClick={closePendingOrderEditor}>
            <div className="max-h-[94vh] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-800 bg-slate-950/95 px-4 py-4 backdrop-blur">
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.18em] text-amber-300">ORDEM PENDENTE #{pendingEditOrder.id}</p>
                  <h3 className="mt-1 text-xl font-black text-white">Gerenciar ordem programada</h3>
                  <p className="mt-1 text-[10px] text-slate-500">{editInstrument === "CAFE_CONILON" ? "Café Conilon" : "Café Arábica"} • {editSide === "buy" ? "COMPRA" : "VENDA"} • {formatBrl(pendingEditOrder.amount)} reservado</p>
                </div>
                <button type="button" onClick={closePendingOrderEditor} disabled={pendingEditSaving} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-white"><X className="h-5 w-5" /></button>
              </div>

              <div className="space-y-4 p-4 sm:p-5">
                <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-900 p-1.5">
                  {[
                    ["limit", "LIMITE", "Executa no seu preço limite"],
                    ["stop", "STOP", "Executa ao atingir o gatilho"],
                  ].map(([type, label, sub]) => (
                    <button key={type} type="button" onClick={() => { setPendingEditType(type); setPendingEditError(""); }} className={`rounded-xl border px-3 py-3 text-left transition ${pendingEditType === type ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200" : "border-transparent text-slate-500"}`}>
                      <span className="block text-xs font-black">{label}</span>
                      <span className="mt-0.5 block text-[8px]">{sub}</span>
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-black text-white">{pendingEditType === "limit" ? "Preço limite" : "Preço de disparo"}</p>
                      <p className="mt-0.5 text-[9px] text-slate-500">Preço atual da saca: {editFair > 0 ? formatBrl(editFair) : "—"}</p>
                    </div>
                    <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2 py-1 text-[8px] font-black text-amber-300">EDITÁVEL</span>
                  </div>
                  <PricePercentSlider
                    label={pendingEditType === "limit" ? "Novo preço limite" : "Novo gatilho"}
                    hint="Arraste pela porcentagem ou digite o preço diretamente."
                    value={pendingEditTrigger}
                    onChange={setPendingEditTrigger}
                    referencePrice={editFair}
                    accent="cyan"
                  />
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3">
                  <div className="mb-3">
                    <p className="text-xs font-black text-white">Stop Loss e Take Profit</p>
                    <p className="mt-0.5 text-[9px] text-slate-500">A referência abaixo é o preço de gatilho da ordem. Deixe vazio para remover uma proteção.</p>
                  </div>
                  <div className="space-y-2">
                    <PricePercentSlider
                      label="Stop Loss"
                      hint={editSide === "buy" ? "Na compra, deve ficar abaixo do gatilho." : "Na venda, deve ficar acima do gatilho."}
                      value={pendingEditStopLoss}
                      onChange={setPendingEditStopLoss}
                      referencePrice={protectionRef}
                      accent="red"
                    />
                    <PricePercentSlider
                      label="Take Profit"
                      hint={editSide === "buy" ? "Na compra, deve ficar acima do gatilho." : "Na venda, deve ficar abaixo do gatilho."}
                      value={pendingEditTakeProfit}
                      onChange={setPendingEditTakeProfit}
                      referencePrice={protectionRef}
                      accent="emerald"
                    />
                  </div>
                  <button type="button" onClick={() => { setPendingEditStopLoss(""); setPendingEditTakeProfit(""); }} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-[10px] font-black text-slate-300 hover:bg-slate-800">REMOVER SL E TP</button>
                </div>

                <div className="rounded-xl border border-slate-800 bg-slate-900/50 px-3 py-2.5 text-[10px] leading-relaxed text-slate-500">
                  Alterar a ordem não muda o valor reservado nem a direção Compra/Venda. Se o novo gatilho já estiver atingido pelo mercado, a ordem poderá ser executada na próxima atualização.
                </div>

                {pendingEditError && (
                  <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-xs text-red-300">{pendingEditError}</div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" onClick={closePendingOrderEditor} disabled={pendingEditSaving} className="h-12 rounded-2xl bg-slate-800 text-xs font-black text-white hover:bg-slate-700">VOLTAR</Button>
                  <Button type="button" onClick={savePendingOrderEdit} disabled={pendingEditSaving || editTriggerNum <= 0} className="h-12 rounded-2xl bg-cyan-500 text-xs font-black text-slate-950 hover:bg-cyan-400 disabled:opacity-40">{pendingEditSaving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}</Button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ===== COFY LOCK ===== */}
      <CofyLockPanel
        open={showCofyLock}
        onClose={() => {
          setShowCofyLock(false);
          fetchLockDashboardSummary({ silent: true });
        }}
        accountMode={accountMode}
      />

      {/* ===== Painel Minha Parceria ===== */}
      {showPartnerPanel && partnerData?.is_partner === true && (
        <div
          className="fixed inset-0 z-[160] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowPartnerPanel(false)}
        >
          <div
            className="max-h-[92%] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-md sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-4 py-4 backdrop-blur">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-400">
                  Programa de parceiros
                </p>
                <h3 className="mt-1 text-xl font-black text-white">Minha Parceria</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPartnerPanel(false)}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-slate-300"
                aria-label="Fechar Minha Parceria"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-4">
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Comissão disponível
                </p>
                <p className="mt-1 text-3xl font-black text-emerald-300">
                  {formatBrl(partnerAvailable)}
                </p>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-400">Sua participação</span>
                  <strong className="rounded-lg bg-slate-950 px-2.5 py-1.5 text-emerald-300">
                    {partnerCommissionPct.toFixed(2)}% do spread
                  </strong>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Indicados</p>
                  <p className="mt-1 text-xl font-black text-white">{partnerReferralCount}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Total comissões</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerTotalCommission)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Spread gerado</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerSpreadGenerated)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Pago</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerPaid)}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Indicação
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-500">Código atual</p>
                    <strong className="block truncate text-lg tracking-wider text-white">
                      {partnerCode || "--"}
                    </strong>
                  </div>
                  <button
                    type="button"
                    onClick={handleShareReferral}
                    disabled={!partnerReferralLink}
                    className="shrink-0 rounded-xl bg-emerald-500 px-4 py-3 text-xs font-black text-slate-950 disabled:opacity-50"
                  >
                    Compartilhar
                  </button>
                </div>
                {partnerReferralLink && (
                  <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5">
                    <p className="text-[9px] uppercase tracking-wider text-slate-600">Seu link</p>
                    <p className="mt-1 break-all text-[11px] font-semibold text-cyan-300">
                      {partnerReferralLink}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Últimas comissões
                  </p>
                  <span className="text-[10px] text-slate-600">máx. 10</span>
                </div>

                {partnerCommissions.length > 0 ? (
                  <div className="space-y-2">
                    {partnerCommissions.slice(0, 10).map((item, index) => (
                      <div
                        key={item?.id ?? index}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900 px-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-white">
                            {String(item?.event_type || "").toLowerCase() === "close"
                              ? "Fechamento"
                              : "Abertura"}{" "}
                            da operação #{item?.order_id ?? "--"}
                          </p>
                          <p className="mt-0.5 truncate text-[9px] text-slate-500">
                            {item?.referred_name || `Usuário #${item?.referred_user_id ?? "--"}`}
                            {item?.created_at ? ` • ${item.created_at}` : ""}
                          </p>
                        </div>
                        <strong className="shrink-0 text-sm text-emerald-300">
                          +{formatBrl(item?.commission_amount ?? 0)}
                        </strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-center text-xs text-slate-500">
                    Nenhuma comissão registrada ainda.
                  </div>
                )}
              </div>

              {partnerLoading && (
                <p className="text-center text-xs text-slate-500">Atualizando parceria...</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Confirmação antes de abrir operação ===== */}
      {pendingTradeConfirm && (
        <div className="fixed inset-0 z-[145] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => !buyLoading && !sellLoading && setPendingTradeConfirm(null)}>
          <div className={`w-full max-w-sm overflow-hidden rounded-3xl border bg-slate-950 shadow-2xl ${pendingTradeConfirm.side === "buy" ? "border-emerald-500/30" : "border-red-500/30"}`} onClick={(e) => e.stopPropagation()}>
            <div className={`p-5 text-center ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
              <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                {pendingTradeConfirm.side === "buy" ? <TrendingUp className="h-7 w-7" /> : <TrendingDown className="h-7 w-7" />}
              </div>
              <h3 className="mt-3 text-xl font-black text-white">{pendingTradeConfirm.orderType === "limit" ? "Confirmar ordem Limite" : pendingTradeConfirm.orderType === "stop" ? "Confirmar ordem Stop" : "Confirmar operação"}</h3>
              <p className={`mt-1 text-sm font-bold ${pendingTradeConfirm.side === "buy" ? "text-emerald-300" : "text-red-300"}`}>{pendingTradeConfirm.side === "buy" ? "COMPRA • ALTA" : "VENDA • BAIXA"}</p>
            </div>
            <div className="p-5">
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                <div className="flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Valor da operação</span><strong className="text-lg text-white">{formatBrl(pendingTradeConfirm.amount)}</strong></div>
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Preço aproximado de entrada</span><strong className="text-white">{formatBrl(pendingTradeConfirm.side === "buy" ? buyPrice : sellPrice)}</strong></div>
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Quantidade estimada</span><strong className="text-white">{(pendingTradeConfirm.side === "buy" ? estimatedBuyCofy : estimatedSellCofy).toFixed(4)} saca(s)</strong></div>
                {["limit", "stop"].includes(pendingTradeConfirm.orderType) && (
                  <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">{pendingTradeConfirm.orderType === "limit" ? "Preço limite" : "Preço de disparo"}</span><strong className="text-cyan-300">{formatBrl(pendingTradeConfirm.triggerPrice)}</strong></div>
                )}
                {pendingTradeConfirm.stopLoss ? (
                  <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Stop Loss</span><strong className="text-red-300">{formatBrl(pendingTradeConfirm.stopLoss)}</strong></div>
                ) : null}
                {pendingTradeConfirm.takeProfit ? (
                  <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Take Profit</span><strong className="text-emerald-300">{formatBrl(pendingTradeConfirm.takeProfit)}</strong></div>
                ) : null}
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Spread</span><strong className="text-white">{spreadPct.toFixed(2)}%</strong></div>
              </div>
              <Button type="button" onClick={() => { const side = pendingTradeConfirm.side; if (side === "buy") handleBuy(); else handleSell(); }} disabled={buyLoading || sellLoading} className={`mt-4 h-14 w-full rounded-2xl text-base font-black ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-600"}`}>{pendingTradeConfirm.side === "buy" ? "CONFIRMAR COMPRA" : "CONFIRMAR VENDA"}</Button>
              <Button type="button" onClick={() => setPendingTradeConfirm(null)} disabled={buyLoading || sellLoading} className="mt-2 h-14 w-full rounded-2xl bg-slate-800 text-base font-black text-white hover:bg-slate-700">CANCELAR</Button>
              <p className="mt-3 text-center text-[10px] leading-relaxed text-slate-500">A operação só será enviada após a confirmação acima.</p>
            </div>
          </div>
        </div>
      )}

      {/* ===== Cadastro para saque ===== */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-3">
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-white">
                  {withdrawProfileCompleted ? "Meu cadastro" : "Completar cadastro para saque"}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  Estes dados são usados para liberar e facilitar seus saques via PIX.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowProfileModal(false);
                  setProfileError("");
                }}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                aria-label="Fechar cadastro"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4 p-4">
              {profileLoading && (
                <p className="text-xs text-slate-400">Carregando cadastro...</p>
              )}

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Nome completo</label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">E-mail</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full cursor-not-allowed rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-400"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs text-slate-300">CPF</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatCpfInput(profileData.cpf)}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        cpf: e.target.value.replace(/\D/g, "").slice(0, 11),
                        ...(prev.pix_key_type === "cpf"
                          ? { pix_key: e.target.value.replace(/\D/g, "").slice(0, 11) }
                          : {}),
                      }))
                    }
                    placeholder="000.000.000-00"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-300">Telefone / WhatsApp</label>
                  <input
                    type="text"
                    inputMode="tel"
                    value={formatPhoneInput(profileData.phone)}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 11),
                      }))
                    }
                    placeholder="(00) 00000-0000"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Data de nascimento</label>
                <input
                  type="date"
                  value={profileData.birth_date}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      birth_date: e.target.value,
                    }))
                  }
                  required
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-[1fr_92px] gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-300">Cidade</label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-300">UF</label>
                  <input
                    type="text"
                    maxLength={2}
                    value={profileData.state}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        state: e.target.value
                          .replace(/[^a-zA-Z]/g, "")
                          .slice(0, 2)
                          .toUpperCase(),
                      }))
                    }
                    placeholder="MG"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm uppercase text-white outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Tipo da chave PIX</label>
                <select
                  value={profileData.pix_key_type}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      pix_key_type: e.target.value,
                      pix_key:
                        e.target.value === "cpf" ? prev.cpf : "",
                    }))
                  }
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                >
                  <option value="cpf">CPF</option>
                  <option value="email">E-mail</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Chave aleatória</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Chave PIX</label>
                <input
                  type={
                    profileData.pix_key_type === "email" ? "email" : "text"
                  }
                  value={
                    profileData.pix_key_type === "cpf"
                      ? formatCpfInput(profileData.cpf)
                      : profileData.pix_key
                  }
                  disabled={profileData.pix_key_type === "cpf"}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      pix_key: e.target.value,
                    }))
                  }
                  placeholder={
                    profileData.pix_key_type === "email"
                      ? "seu@email.com"
                      : profileData.pix_key_type === "phone"
                      ? "Telefone cadastrado no PIX"
                      : profileData.pix_key_type === "random"
                      ? "Cole sua chave aleatória"
                      : "CPF do cadastro"
                  }
                  required
                  className={[
                    "w-full rounded-lg border px-3 py-2.5 text-sm outline-none",
                    profileData.pix_key_type === "cpf"
                      ? "cursor-not-allowed border-slate-800 bg-slate-900/60 text-slate-400"
                      : "border-slate-700 bg-slate-900 text-white focus:border-emerald-500",
                  ].join(" ")}
                />
              </div>

              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-[11px] leading-relaxed text-amber-100/80">
                  Confira sua chave PIX antes de salvar. Ela será usada para facilitar os próximos saques.
                </p>
              </div>

              {profileError && (
                <p className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-300">
                  {profileError}
                </p>
              )}

              <Button
                type="submit"
                disabled={profileSaving || profileLoading}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-400 font-bold text-slate-950"
              >
                {profileSaving
                  ? "Salvando..."
                  : withdrawProfileCompleted
                  ? "Salvar alterações"
                  : "Completar cadastro e continuar"}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* ===== Ajuda COFY via WhatsApp ===== */}
      <button
        type="button"
        onClick={openCofyWhatsApp}
        className="fixed bottom-4 right-4 z-[91] flex h-12 items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500 px-4 text-sm font-black text-slate-950 shadow-xl shadow-emerald-500/20 transition hover:bg-emerald-400 hover:scale-105 active:scale-95"
        aria-label="Falar com a COFY pelo WhatsApp"
        title="Ajuda pelo WhatsApp"
      >
        <MessageCircle className="h-5 w-5" />
        <span>Ajuda</span>
      </button>

      <DepositModal open={showDeposit} onClose={() => setShowDeposit(false)} onSuccess={handleDepositSuccess} />
      <WithdrawalModal
        open={showWithdrawal}
        onClose={() => setShowWithdrawal(false)}
        onSuccess={handleWithdrawalSuccess}
        balance={balance}
      />
    </div>
  );
}